import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { queryClient, queryKeys, useQueryState } from '../lib/queryClient';
import {
  authenticateUser,
  registerUser,
  sendRegisterCode as apiSendRegisterCode,
  resendRegisterCode as apiResendRegisterCode,
  sendPasswordReset as apiSendPasswordReset,
  resetPasswordWithCode as apiResetPasswordWithCode,
  fetchSecurity,
  verifyLoginCode,
  verifyAuthenticatorCode,
  logout as apiLogout,
  type PasswordResetRequest,
} from '../api/authApi';
import { defaultPasskeyLabel, registerPasskey, signInWithPasskey } from '../api/passkeyApi';
import { CueClient } from '../lib/cue/client';
import type { ChartSettings } from '../components/charts/SignalActivityChart';
import { ROUND_TYPE, roundActionLabel, type RoundAction } from '../lib/round';
import {
  cloneChannel as apiCloneChannel,
  cloneTemplate as apiCloneTemplate,
  publishChannelAsTemplate as apiPublishChannelAsTemplate,
  createChannel as apiCreateChannel,
  createTemplate as apiCreateTemplate,
  deleteChannel as apiDeleteChannel,
  clearChannelMessages as apiClearChannelMessages,
  deleteTemplate as apiDeleteTemplate,
  fetchChannel,
  fetchChannels,
  fetchTemplate,
  fetchChannelInvites,
  fetchChannelMember,
  fetchUserProfileById,
  acceptChannelInvite,
  removeChannelMember,
  fetchSignals as apiFetchSignals,
  fetchTemplates,
  fetchMappings,
  createMapping as apiCreateMapping,
  deleteMapping as apiDeleteMapping,
  upsertMapping as apiUpsertMapping,
  rotateMappingIngestToken as apiRotateMappingIngestToken,
  rotateMappingWebhookSecret as apiRotateMappingWebhookSecret,
  setMappingEnabled as apiSetMappingEnabled,
  fetchUnreadCounts,
  markChannelRead,
  pinChannel as apiPinChannel,
  unpinChannel as apiUnpinChannel,
  type UnreadCounts,
  type FetchedSignal,
  normalizeFetchedSignal,
  sendSignal as apiSendSignal,
  upsertChannel as apiUpsertChannel,
  upsertTemplate as apiUpsertTemplate,
  setUnauthorizedHandler,
  renameChannel as apiRenameChannel,
  checkChannelNicknameAvailable as apiCheckChannelNicknameAvailable,
  renameTemplate as apiRenameTemplate,
  checkTemplateNicknameAvailable as apiCheckTemplateNicknameAvailable,
} from '../api/dataApi';
import { fetchProfile, syncProfile, renameNickname as apiRenameNickname, checkUserNicknameAvailable as apiCheckUserNicknameAvailable } from '../api/profileApi';
import {
  fetchBillingState,
  fetchTierCatalog,
  openBillingPortal as apiOpenBillingPortal,
  startCheckout as apiStartCheckout,
} from '../api/billingApi';
import {
  uid,
  REPORT_TYPE,
  REPORT_JOB_ACTIVE,
  AI_PROVIDERS,
  DEFAULT_AI_PROVIDER,
  emptyAiKeys,
  type AccountTier,
  type AIProvider,
  type AISettingsDraft,
  type AppLanguage,
  type BillingInterval,
  type BillingState,
  type Channel,
  type ChannelRef,
  type ChannelViewType,
  type ChartVariant,
  type Mapping,
  type Message,
  type Post,
  type ProfileDraft,
  type SecurityDraft,
  type Template,
  type TemplateSignal,
  type ThemeMode,
  type TierCatalogEntry,
  type Workflow,
  type AiJobPriority,
  type AiReportPeriodDays,
  type ReportJob,
  type ReportJobStatus,
} from '../types';
import { ChannelUpdateKind, CueCommand, parseChannelUpdateKind } from '../lib/cue/wire';
import { appLog, appWarn } from '../lib/logger';
import { MessageStore, useChannelFeedFrom, type Feed } from './messageStore';
import * as signalDb from '../lib/signalDb';
import {
  cancelReportJob as apiCancelReportJob,
  enqueueReportJob as apiEnqueueReportJob,
  fetchChannelQuestions as apiFetchChannelQuestions,
  removeReportQuestion as apiRemoveReportQuestion,
  suggestReportQuestions as apiSuggestReportQuestions,
  fetchReportJobs as apiFetchReportJobs,
} from '../api/reportApi';

const LS = {
  email: 'email',
  themeMode: 'themeMode',
  language: 'language',
  aiProvider: 'ai.provider',
  aiKeyPrefix: 'ai.key.', // + <provider>
  aiDeepseekKeyLegacy: 'ai.deepseekApiKey',
  securityKey: 'security.key',
  securitySecret: 'security.secret',
  profile: 'profile.v1',
  channelsCache: 'channels.cache.v1',
  templatesCache: 'templates.cache.v1',
  mappingsCache: 'mappings.cache.v1',
  postLoginRedirect: 'postLoginRedirect',
  threeColumnLayout: 'threeColumnLayout',
  manualRefresh: 'manualRefresh',
  diagnosticsMenuEnabled: 'diagnosticsMenuEnabled',
};

// Lets a screen outside the auth flow (currently only the public shared-
// channel view's Join button) send a visitor to /login or /register and get
// routed back afterward, instead of always landing on /channels - see
// App.tsx's RequireGuest, the only reader of this.
export function rememberPostLoginRedirect(path: string) {
  try {
    localStorage.setItem(LS.postLoginRedirect, path);
  } catch {
    // storage full/unavailable/private-browsing - worst case, lands on /channels
  }
}

export function consumePostLoginRedirect(): string | null {
  try {
    const value = localStorage.getItem(LS.postLoginRedirect);
    if (value) localStorage.removeItem(LS.postLoginRedirect);
    return value;
  } catch {
    return null;
  }
}

// ---- memory limits ---------------------------------------------------------
// Messages held per channel: the open channel keeps a deep window (scroll-back
// history), every other channel just its newest page - the rest is re-fetchable
// on demand (loadMoreMessages pages by offset from the newest).
const MAX_MESSAGES_ACTIVE = 1000;
const MAX_MESSAGES_IDLE = 40;
// Read-only "viewed" (unfollowed public) channels kept in memory besides the open one.
const MAX_VIEWED_CHANNELS = 5;
// How long after leaving a channel before idle channels are trimmed.
const IDLE_TRIM_DELAY_MS = 30_000;
// How long localStorage cache writes
// are coalesced (a streaming channel changes state many times a second).
// Newest messages per channel seeded from IndexedDB on start (before the network answers).
const SEED_MESSAGES_PER_CHANNEL = 40;
// Live signals are merged in one batch per window instead of one state change each.
const LIVE_SIGNAL_COALESCE_MS = 300;
const CACHE_WRITE_DEBOUNCE_MS = 1000;

// While a channel stays open and signals keep streaming in, the server is told
// "read" at most this often (the local badge is zeroed at once regardless), and
// the unread counts re-pulled at most this often.
const MARK_READ_MIN_GAP_MS = 20_000;
const UNREAD_REFRESH_GAP_MS = 20_000;

// Minimum gap between relay-triggered signal refetches of one channel.
const RELAY_REFRESH_WINDOW_MS = 1500;

// Parses the inline signal a REST-written "m" relay carries (see the relay
// handler). null for anything else - a Cue-typed message's plain text, the
// bare `{"k":"signal"}` hint, malformed JSON - which all take the refetch path.
function parseInlineSignal(value: string): FetchedSignal | null {
  if (!value || value[0] !== '{') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed?.k === 'signal' && parsed.s ? normalizeFetchedSignal(parsed.s) : null;
  } catch {
    return null;
  }
}

function readLS(key: string, fallback = ''): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

// Backs the "read from backend, cache it for when the connection drops"
// behavior: the last successfully fetched channel/template list is mirrored
// into localStorage on every change, so a reload with no network (or a
// backend that's down, or any other fetch failure) falls back to the last
// known-good data instead of starting empty.
function loadCache<T>(key: string): T[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : null;
  } catch {
    return null;
  }
}

function saveCache<T>(key: string, value: T[]) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full/unavailable/private-browsing - caching is best-effort
  }
}

// The single channel-list order, shared by every screen that renders the
// list. Ascending - lowest key floats to the top:
//
//   1. channel.priority          admin, ZMAP-set, default 0. A big negative
//                                pins a channel above everything.
//   2. pins[nickname] (or 0)     user pin score, negative, most-recent pin
//                                most negative -> top of the pinned block.
//   3. unread ? 0 : 1            channels with unread signals first.
//   4. -opens[nickname] (or 0)   opened more recently -> higher; a channel
//                                never opened (0) sorts after any that was.
//   5. title (case-insensitive), then nickname - deterministic tiebreak.
//
// The backend (channel/list.wsl -> pins.SortAndPaginateChannels) already
// sorts by 1/2/4/5 so pagination is stable; this re-applies the full
// comparator (adding 3) over whatever is loaded so the order stays right
// as unread/open state changes locally. Mirrors iOS ZmistStore.sortedChannels.
function sortChannels(
  list: Channel[],
  pins: Record<string, number>,
  opens: Record<string, number>,
  unreadByChannel: Record<string, number>
): Channel[] {
  if (!Array.isArray(list)) return [];
  const key = (c: Channel): [number, number, number, number, string, string] => [
    c.priority ?? 0,
    pins[c.nickname] ?? 0,
    (unreadByChannel[c.nickname] ?? 0) > 0 ? 0 : 1,
    -(opens[c.nickname] ?? 0),
    (c.title || '').toLowerCase(),
    c.nickname,
  ];
  return [...list].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    for (let i = 0; i < ka.length; i++) {
      if (ka[i] < kb[i]) return -1;
      if (ka[i] > kb[i]) return 1;
    }
    return 0;
  });
}

interface StoreState {
  isLoggedIn: boolean;
  // True once the boot-time "am I logged in" check has resolved (see its
  // useEffect's comment) - App.tsx's RequireAuth/RequireGuest gate on
  // this so a real session doesn't flash a guest screen (or the reverse)
  // before that first check lands.
  authChecked: boolean;
  selectedTab: 'channels' | 'showcase' | 'templates' | 'settings';
  themeMode: ThemeMode;
  language: AppLanguage;
  // Persisted layout choice for ChatScreen's Messages/Charts/Reports panes
  // - true (default) keeps its existing xl:grid-cols-3 side-by-side
  // behavior once the viewport is wide enough; false forces the single-
  // pane tab-switcher view regardless of how wide the screen is.
  threeColumnLayout: boolean;
  // Whether the Diagnostics screen is exposed as a main navigation tab
  // (sidebar + mobile bottom bar). Default false - Diagnostics is an
  // opt-in tool, toggled from Settings > Connection. Device-local
  // (localStorage only, like threeColumnLayout).
  diagnosticsMenuEnabled: boolean;
  // "Data saver" / manual-refresh mode (Settings > Connection). Default
  // false - the app refreshes lists automatically on launch, on login,
  // whenever a list/chat screen is opened, and polls running AI report
  // jobs. Flipped true, every one of those automatic network fetches is
  // suppressed: the app shows whatever is in the local cache and only
  // pulls fresh data when the user explicitly asks (the refresh buttons,
  // hidden by default, all become visible). The live Cue connection -
  // sending and receiving messages/signals - is unaffected either way.
  // A device-local preference (localStorage only, like the two above).
  manualRefresh: boolean;
  channels: Channel[];
  templates: Template[];
  // The caller's own integration mappings (GET /mappings is owner-scoped).
  mappings: Mapping[];
  // Whether another page exists beyond what's currently loaded in
  // `channels`/`templates` above - drives the infinite-scroll sentinel in
  // ChannelsListScreen.tsx/TemplatesListScreen.tsx. Defaults true (assume
  // there might be more) until the first real fetch confirms otherwise.
  channelsHasMore: boolean;
  templatesHasMore: boolean;
  mappingsHasMore: boolean;
  // Message history lives in an external per-channel store (see
  // messageStore.ts), not in React state: read it with useChannelFeed(id) so
  // only the components showing that channel re-render on a new signal.
  messageStore: MessageStore;
  profile: ProfileDraft;
  security: SecurityDraft;
  aiSettings: AISettingsDraft;
  // Pricing-tier catalog (GET /billing/tiers) and the caller's live billing
  // state (GET /billing/me) - both best-effort, populated after login by the
  // billing effect. `billing` is null until it first lands (or if the
  // fetch fails); `tierCatalog` is [] until then. profile.tier holds the
  // quick tier id for gating; `billing` carries usage + limits + period end
  // for the Plan screen.
  tierCatalog: TierCatalogEntry[];
  billing: BillingState | null;
  searchQuery: string;
  errorMessage: string | null;
  // True while any queryClient.query call from syncChannels/
  // syncTemplates/loadMoreChannels/loadMoreTemplates/loadMessages/
  // loadMoreMessages is in flight (see lib/queryClient.ts) - a single,
  // centralized signal instead of each screen tracking its own loading
  // useState for the same underlying network activity. A screen that
  // wants to know about ONLY its own fetch can still just await the
  // relevant function directly; this is for a global "something's
  // loading" indicator.
  isSyncing: boolean;
}

// Returned by login() when the account has email 2FA enabled - the
// password was accepted but no session was issued yet.
export interface TwoFactorChallenge {
  challengeId: string;
  email: string;
  // 'authenticator' when the account uses a TOTP app instead of email
  // (verifyTwoFactor still expects an email code - call verifyAuthenticator
  // for this kind instead).
  kind: 'email' | 'authenticator';
}

// One pending channel invite, as shown on ChannelInvitesScreen - the
// hydrated channel plus the display name/nickname of whoever invited you
// (resolved from the member doc's invitedBy userId; blank if unresolvable).
export interface ChannelInvite {
  channel: Channel;
  invitedByName: string;
  invitedByNickname: string;
}

interface StoreContextValue extends StoreState {
  setSelectedTab: (tab: StoreState['selectedTab']) => void;
  setSearchQuery: (q: string) => void;
  setErrorMessage: (m: string | null) => void;

  // Resolves to a TwoFactorChallenge when the account has email 2FA on
  // (caller must then call verifyTwoFactor); resolves to void on a normal
  // one-step login.
  login: (email: string, password: string) => Promise<TwoFactorChallenge | void>;
  verifyTwoFactor: (challengeId: string, code: string) => Promise<void>;
  verifyAuthenticator: (challengeId: string, code: string) => Promise<void>;
  loginWithPasskey: () => Promise<void>;
  registerWithPasskey: (name: string, email: string, challengeId: string, code: string) => Promise<void>;
  loginWithToken: (email: string) => void;
  register: (name: string, email: string, password: string, challengeId: string, code: string) => Promise<void>;
  sendRegisterCode: (email: string, turnstileToken: string) => Promise<string>;
  resendRegisterCode: (challengeId: string) => Promise<string>;
  sendPasswordReset: (email: string) => Promise<PasswordResetRequest>;
  resetPasswordWithCode: (challengeId: string, code: string, password: string, confirm: string) => Promise<string>;
  logout: () => void;

  setThemeMode: (m: ThemeMode) => void;
  setLanguage: (l: AppLanguage) => void;
  setThreeColumnLayout: (on: boolean) => void;
  setManualRefresh: (on: boolean) => void;
  setDiagnosticsMenuEnabled: (on: boolean) => void;
  setProfile: (p: ProfileDraft) => void;
  setSecurity: (s: SecurityDraft) => void;
  setAiSettings: (a: AISettingsDraft) => void;
  syncSettings: (p: ProfileDraft, m: ThemeMode, l: AppLanguage) => Promise<void>;

  // Billing / pricing tiers. refreshBilling re-pulls GET /billing/me (call
  // it after returning from Stripe Checkout). startUpgrade kicks off a
  // Stripe Checkout for a paid tier and redirects the browser to it;
  // openBillingPortalPage opens the Stripe billing portal in a new tab.
  refreshBilling: () => Promise<void>;
  startUpgrade: (tierId: AccountTier, interval?: BillingInterval) => Promise<void>;
  openBillingPortalPage: () => Promise<void>;

  syncChannels: () => Promise<void>;
  syncTemplates: () => Promise<void>;
  syncMappings: () => Promise<void>;
  // Manual reconnect - e.g. ConnectionDot's onClick when a channel's Cue
  // status is red/amber - instead of waiting out the client's own backoff.
  // A no-op (not a crash) before real credentials/a client exist yet.
  reconnectCue: () => void;
  // Pending channel invites (an owner "added" you; you accept or decline).
  // channelInvites is hydrated by syncChannelInvites (GET /channels/invites
  // then per-nickname fetchChannel + inviter lookup); acceptInvite makes
  // you a real member and drops the invite; declineInvite just drops it.
  // See ChannelInvitesScreen.tsx and backend/workflows/channel/
  // invite_accept.wsl / invite_list.wsl.
  channelInvites: ChannelInvite[];
  syncChannelInvites: () => Promise<void>;
  acceptInvite: (channelNickname: string) => Promise<void>;
  declineInvite: (channelNickname: string) => Promise<void>;
  // Fetch the next page (offset = however many are already loaded) and
  // append - unlike syncChannels/syncTemplates above, which always
  // refetch page 1 and replace. No-op (and a no-op is fine to call
  // freely) once channelsHasMore/templatesHasMore is false.
  loadMoreChannels: () => Promise<void>;
  loadMoreTemplates: () => Promise<void>;
  loadMoreMappings: () => Promise<void>;
  loadMoreMessages: (channelId: string) => Promise<void>;
  messagesHasMore: (channelId: string) => boolean;

  channel: (id: string) => Channel | undefined;
  // `channels` is now scoped to the caller's own (owned/joined/followed)
  // channels - see fetchChannels' comment - so a public channel found via
  // Search but not yet followed/joined isn't in it at all. Call this once
  // (e.g. on mount) for a read-only screen reached from a search result
  // (currently just ChannelDetailsScreen) so `channel(id)` can still
  // resolve it via a direct fetch, cached separately so it doesn't leak
  // into the personal "Channels" list. A no-op if already resolvable or
  // already in flight.
  //
  // Deliberately NOT wired into ChatScreen/MessagesScreen - those mutate
  // `channels` directly (loadMessages/patchChannel/addMessage etc. all
  // `setChannels(prev => prev.map(...))`, a no-op for an id that isn't in
  // `channels`), so a channel resolved only through this cache would
  // silently never receive its messages. Opening the live chat of a
  // public channel you haven't joined needs a real join flow (see the
  // deferred Stage C in the multi-owner-channels plan), not this cache.
  ensureChannelLoaded: (id: string) => void;
  updateChannel: (updated: Channel) => Promise<void>;
  createChannel: (draft: Channel) => Promise<void>;
  deleteChannel: (id: string) => Promise<void>;
  // Genuinely migrates the channel's nickname server-side (see backend
  // internal/rename) - unlike updateChannel, which can't touch it at all.
  // Returns the re-fetched channel (its `id` is the new nickname now, so
  // callers that navigate by id need to redirect to the new URL).
  renameChannel: (id: string, newNickname: string) => Promise<Channel>;
  checkChannelNicknameAvailable: (nickname: string) => Promise<boolean>;
  renameTemplate: (id: string, newNickname: string) => Promise<Template>;
  checkTemplateNicknameAvailable: (nickname: string) => Promise<boolean>;
  renameMyNickname: (newNickname: string) => Promise<void>;
  checkUserNicknameAvailable: (nickname: string) => Promise<boolean>;
  // Owner "clean chat" - deletes every message in the channel (owner only,
  // enforced server-side) and clears its local message list. Returns how
  // many messages were deleted.
  clearChannelMessages: (channelNickname: string) => Promise<number>;
  // A member leaving a channel (self-removal). For a real member (invited
  // & accepted, or an approved join request) - a plain follower unfollows
  // instead. See ChannelDetailsScreen's leave-vs-unfollow split.
  leaveChannel: (channelNickname: string) => Promise<void>;
  isChannelOwner: (c: Channel) => boolean;
  // Copies a (typically public, someone else's) channel into a fresh
  // channel owned by the current user, under a "<nickname>-copy" handle
  // and a "<title> (copy)" title. Returns the created channel so the
  // caller can navigate to it.
  cloneChannel: (source: Channel, clickSource?: string) => Promise<Channel>;
  // "Publish as template" - snapshots a channel's attached-template signal
  // palette into a new, standalone, owner-private Template. `signals` is
  // caller-resolved (see ChannelManageScreen.tsx's selectedTemplates) -
  // see dataApi's publishChannelAsTemplate for the full rationale.
  publishChannelAsTemplate: (source: Channel, signals: TemplateSignal[]) => Promise<Template>;

  updateTemplate: (updated: Template) => Promise<void>;
  addTemplate: (t: Template) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  isTemplateOwner: (t: Template) => boolean;
  // See cloneChannel - the template equivalent.
  cloneTemplate: (source: Template, clickSource?: string) => Promise<Template>;
  // See applyChannelStats' comment - the template equivalent.
  applyTemplateStats: (templateId: string, patch: Partial<Pick<Template, 'liked' | 'likes'>>) => void;

  addMapping: (m: Mapping) => Promise<Mapping>;
  updateMapping: (updated: Mapping) => Promise<Mapping>;
  deleteMapping: (nickname: string) => Promise<void>;
  rotateMappingIngestToken: (nickname: string) => Promise<Mapping>;
  rotateMappingWebhookSecret: (nickname: string) => Promise<Mapping>;
  setMappingEnabled: (nickname: string, enabled: boolean) => Promise<Mapping>;
  isMappingOwner: (m: Mapping) => boolean;

  updateWorkflow: (updated: Workflow, channelId: string) => void;

  addMessage: (m: Message, channelId: string) => void;
  sendMessage: (
    text: string,
    channelId: string,
    type?: string,
    value?: string,
    signalNumber?: string,
    attachment?: Message['attachment'],
    replyToId?: string
  ) => void;
  // The owner opens/reveals/hides/closes a "reveal round" (planning poker /
  // hidden voting). Posts a type "round" marker; deriveRound (lib/round.ts)
  // reconstructs the phase from the timeline. See ChatScreen / MessagesScreen.
  sendRoundAction: (channelId: string, action: RoundAction) => Promise<void>;
  loadMessages: (channelId: string) => Promise<void>;
  subscribeToChannel: (channelId: string) => () => void;
  addPost: (p: Post, channelId: string) => void;

  // AI report jobs (owner-only, per channel). loadReportJobs seeds the
  // first page; loadMoreReportJobs appends the next one (infinite scroll in
  // the Reports panel); enqueueReportJob queues one; cancelReportJob stops
  // a queued or running one. Live status arrives via the Cue "rj" relay.
  // reportJobsMeta carries the retained total + a hasMore/loadingMore flag
  // per channel so the panel knows whether to keep paging.
  reportJobs: Record<string, ReportJob[]>;
  reportJobsMeta: Record<string, { total: number; hasMore: boolean; loadingMore: boolean }>;
  loadReportJobs: (channelId: string) => Promise<void>;
  loadMoreReportJobs: (channelId: string) => Promise<void>;
  enqueueReportJob: (
    channelId: string,
    opts: {
      provider: AIProvider;
      periodDays: AiReportPeriodDays;
      priority: AiJobPriority;
      apiKey?: string;
      additionalChannelIds?: string[];
      mode?: 'questions';
      questions?: string[];
    }
  ) => Promise<ReportJob>;
  cancelReportJob: (channelId: string, jobId: string) => Promise<void>;
  // Reports screen "Ask questions" flow: fetchChannelQuestions reads the
  // channel's stored question history with no LLM call (shown the moment
  // the panel opens); suggestReportQuestions is the "Ask AI again" action
  // that actually calls the LLM for a fresh batch (and appends them to
  // that same stored history server-side).
  fetchChannelQuestions: (channelId: string) => Promise<string[]>;
  // Permanently drops one question from the channel's stored history
  // (exact string match) - the "remove from the list" action. Returns
  // the updated list.
  removeReportQuestion: (channelId: string, question: string) => Promise<string[]>;
  suggestReportQuestions: (
    channelId: string,
    opts: { provider: AIProvider; periodDays: AiReportPeriodDays; apiKey?: string; additionalChannelIds?: string[] }
  ) => Promise<string[]>;

  // Unread signal counts (Phase 1b). `unreadByChannel` is keyed by
  // nickname; `unreadTotal` is the cross-channel sum for the nav badge.
  // refreshUnread re-pulls GET /stats/unread; markChannelViewed records
  // the channel as read server-side and zeroes its local badge optimistically.
  unreadByChannel: Record<string, number>;
  unreadTotal: number;
  refreshUnread: () => Promise<void>;
  markChannelViewed: (channelId: string) => void;

  // Per-user channel pinning - a pinned channel sorts to the top of the
  // list (below any ZMAP-set admin-priority channels). isChannelPinned
  // reads the local pin map; setChannelPinned patches it optimistically
  // and calls POST/DELETE /channels/{nickname}/pin. See sortChannels.
  isChannelPinned: (channelId: string) => boolean;
  setChannelPinned: (channelId: string, pinned: boolean) => Promise<void>;

  updateChannelViewType: (viewType: ChannelViewType, channelId: string) => void;
  updateChannelChartType: (chartType: ChartVariant, channelId: string) => void;
  // ChannelShareScreen's "Let visitors adjust chart filters" toggle - see
  // its implementation's own comment just below.
  updateChannelPublicChartFiltersAllowed: (allowed: boolean, channelId: string) => void;

  // ChartScreen's "Add channel" picker selection - persisted on the primary
  // channel's own doc (Channel.chartCompareChannels, synced via patchChannel
  // same as updateChannelChartType) so it survives a page reload instead of
  // living only in memory. See ChannelRef's comment (types.ts).
  getChartCompareChannels: (channelId: string) => ChannelRef[];
  setChartCompareChannels: (channelId: string, channels: ChannelRef[]) => void;
  // ReportsScreen's "Add channel" picker selection (Multi-Channel Report) -
  // persisted on the primary channel's own doc
  // (Channel.reportAdditionalChannels, synced via patchChannel), same
  // pattern as chartCompareChannels just above, so it survives a reload or
  // a different session instead of resetting to empty every time the
  // screen mounts.
  getReportAdditionalChannels: (channelId: string) => ChannelRef[];
  setReportAdditionalChannels: (channelId: string, channels: ChannelRef[]) => void;
  // Charts tab "Filters & settings" sync - see its implementation's own
  // comment just below.
  setChartSettings: (channelId: string, settings: ChartSettings) => void;
  // Applies a like/follow/isPublic result already confirmed by the
  // backend (see dataApi.ts's likeChannel/unlikeChannel/followChannel/
  // unfollowChannel/enableShareLink/disableShareLink) - a local cache
  // merge only, unlike patchChannel, since none of these need (or should
  // trigger) a whole-channel PATCH back to POST /channel/{nickname} - the
  // server is already the source of truth for the new value by the time
  // this is called. isPublic specifically: enabling/disabling the share
  // link is what actually changes it server-side now (see
  // share_link_enable.wsl's header comment), this just mirrors that
  // confirmed result into the locally-held channel.
  applyChannelStats: (channelId: string, patch: Partial<Pick<Channel, 'liked' | 'following' | 'muted' | 'likes' | 'followers' | 'isPublic' | 'ownerIds'>>) => void;
  // See applyChannelStats' neighboring comment in StoreProvider - inserts
  // a just-followed channel into the personal "Channels" list right away.
  addChannelToPersonalList: (c: Channel) => void;
  // The unfollow-side mirror - drops a just-unfollowed public channel out
  // of the personal list for a non-owner (see its impl comment).
  removeChannelFromPersonalList: (c: Channel) => void;
}

const StoreContext = createContext<StoreContextValue | null>(null);

// The Reports panel shows the newest few AI report jobs and pages older
// ones in as the owner scrolls the (height-capped) list.
const REPORT_JOBS_PAGE = 3;

const defaultProfile: ProfileDraft = {
  userId: '',
  name: '',
  nickname: '',
  phone: '',
  website: '',
  instagram: '',
  whatsapp: '',
  messenger: '',
  telegram: '',
  linkedin: '',
  avatarSymbol: '',
  avatarImageKey: '',
  tier: 'free',
  showServerPipeline: false,
};

const defaultSecurity: SecurityDraft = {
  key: 'QUJDREVGR0hJSktMTU5PUFFSU1RVVldYWVo=',
  secret: 'ZmistSecretKey',
};

const defaultAiSettings: AISettingsDraft = {
  provider: DEFAULT_AI_PROVIDER,
  apiKeys: emptyAiKeys(),
};

function readAiSettings(): AISettingsDraft {
  const provider = (readLS(LS.aiProvider, DEFAULT_AI_PROVIDER) as AIProvider) || DEFAULT_AI_PROVIDER;
  const apiKeys = emptyAiKeys();
  for (const p of AI_PROVIDERS) {
    apiKeys[p] = readLS(LS.aiKeyPrefix + p, '');
  }
  // One-time migration from the single-key layout.
  if (!apiKeys.deepseek) {
    const legacy = readLS(LS.aiDeepseekKeyLegacy, '');
    if (legacy) apiKeys.deepseek = legacy;
  }
  return {
    // A saved provider that is no longer offered (e.g. the removed local Llama) moves to the default.
    provider: AI_PROVIDERS.includes(provider) ? provider : DEFAULT_AI_PROVIDER,
    apiKeys,
  };
}

function loadProfile(): ProfileDraft {
  try {
    const raw = localStorage.getItem(LS.profile);
    if (raw) return { ...defaultProfile, ...JSON.parse(raw) };
  } catch {
    // ignore
  }
  return defaultProfile;
}

// Routes a logged-out visitor can load directly; the boot sync must not call
// session-only endpoints there.
export function isPublicBootPath(pathname: string): boolean {
  return /^\/(public|shared|status|terms|privacy|cookies|subprocessors|dpa|login|register|start)(\/|$)/.test(pathname);
}

export function StoreProvider({ children }: { children: ReactNode }) {
  // Seed from the last successful fetch (cached in localStorage) when one
  // exists, so a reload with no connection shows real data instead of
  // starting empty - only genuinely empty when nothing has ever been
  // cached (first run, or storage unavailable) and the backend has no
  // data yet either.
  //
  // Query-owned state, not plain useState - see lib/queryClient.ts's
  // useQueryState for why (and for the earlier useQuery-as-storage-cell
  // attempt that broke 7 tests on the same-tick-staleness hazard and was
  // reverted rather than guessed through). Every setChannels/setTemplates
  // call below is unchanged from the useState version - useQueryState's
  // setter has the identical (value | updater) => void shape.
  const [templates, setTemplates] = useQueryState<Template[]>(queryKeys.templatesState(), () => loadCache<Template>(LS.templatesCache) ?? []);
  const [channels, setChannels] = useQueryState<Channel[]>(queryKeys.channelsState(), () => loadCache<Channel>(LS.channelsCache) ?? []);
  const [mappings, setMappings] = useQueryState<Mapping[]>(queryKeys.mappingsState(), () => loadCache<Mapping>(LS.mappingsCache) ?? []);
  const [channelsHasMore, setChannelsHasMore] = useState(true);
  const [templatesHasMore, setTemplatesHasMore] = useState(true);
  const [mappingsHasMore, setMappingsHasMore] = useState(true);
  // Unread signal counts (Phase 1b) - transient, re-pulled on login, after
  // every channel sync, and on every Cue relay. Keyed by nickname.
  const [unread, setUnread] = useState<UnreadCounts>({ byChannel: {}, total: 0, pins: {}, opens: {} });
  // Channel-list ordering inputs (see sortChannels below). Both come from
  // the same GET /stats/unread the counts above do, plus optimistic local
  // patches on pin/unpin (channelPins) and on channel open (channelOpenedAt),
  // so the list re-sorts instantly without waiting for a refresh.
  const [channelPins, setChannelPins] = useState<Record<string, number>>({});
  const [channelOpenedAt, setChannelOpenedAt] = useState<Record<string, number>>({});
  // Pending channel invites - transient, always re-fetched on login (not
  // localStorage-cached: a cached invite that was since accepted/revoked
  // would be misleading, and the list is tiny).
  const [channelInvites, setChannelInvites] = useState<ChannelInvite[]>([]);
  // Stable for the provider's lifetime (a ref, so the persistent Cue relay handler's frozen closure still reaches it).
  const messageStoreRef = useRef<MessageStore | null>(null);
  // Per channel: how many entries the server returned that were already loaded (so
  // they did not grow the list). Paging by the loaded count alone would then ask for
  // the same offset forever; the server's own position is count + these repeats.
  const pageRepeatsRef = useRef<Map<string, number>>(new Map());
  if (!messageStoreRef.current) messageStoreRef.current = new MessageStore();
  const messageStore = messageStoreRef.current;
  // A public channel viewed via a Search result but not (yet) followed or
  // joined isn't in `channels` at all - see ensureChannelLoaded below.
  // Kept separate from `channels` (never merged in) so it can't leak an
  // unfollowed public channel into the personal "Channels" list.
  const [viewedChannels, setViewedChannels] = useState<Record<string, Channel>>({});
  const viewedChannelFetchesRef = useRef<Set<string>>(new Set());
  // See syncChannels' own comment - discards an out-of-order-arriving
  // response from an earlier, superseded syncChannels() call.
  const syncChannelsGenRef = useRef(0);

  // Best-effort: if a server is reachable, prefer its data so edits persist
  // across reloads. An empty channel/template list from a fresh backend
  // doesn't overwrite the cached data above - only a non-empty result does,
  // so a stale cache from a since-emptied backend doesn't linger forever,
  // and a failed fetch (offline, backend down, anything else) just leaves
  // the cached data in place. Passing the current state as `existing` lets the
  // refetch re-attach local-only fields onto the fresh data instead of
  // resetting them - otherwise reconnecting after an outage would wipe out
  // exactly what the cache was meant to preserve.
  // Pulled out of the bootstrap effect below so a manual "sync" button
  // (channels/templates list screens) can re-run the exact same fetch on
  // demand, not just once at launch - same non-destructive behavior either
  // way (only a non-empty result overwrites what's on screen).
  // Routes through queryClient.query (see lib/queryClient.ts's own
  // comment) instead of calling fetchChannels directly - two overlapping
  // triggers for the SAME page (a mount effect and a manual sync button
  // tap landing close together, say) now share one in-flight request and
  // one retry-with-backoff policy, instead of each firing its own fetch
  // and racing to setChannels last. `channels` itself stays a plain
  // useState, mutated exactly as before - only the network call
  // underneath is now coordinated.
  const syncChannels = async () => {
    // Guards against two overlapping syncChannels() calls resolving out of
    // order - e.g. a rapid Follow/Unfollow/Follow click burst, where each
    // toggle's own "cl" Cue echo (see stats/channel_follow.wsl's
    // BroadcastFollowToUser) fires its own syncChannels() call. Those
    // aren't concurrent enough for queryClient's own dedup (same queryKey,
    // but each call starts after the previous one's request already went
    // out), so without this an OLDER call's response landing AFTER a
    // newer one's would overwrite fresher data with stale counts - visibly
    // a channel's follower count (or anything else GET /channels reports)
    // flickering to an outdated value a moment after settling on the
    // right one. Only the response from the MOST RECENTLY STARTED call is
    // ever applied; every earlier one is dropped on arrival.
    const gen = ++syncChannelsGenRef.current;
    appLog('[Sync] syncChannels(): starting');
    const page = await queryClient.query({
      queryKey: queryKeys.channels(0),
      queryFn: () => fetchChannels(channels),
      // Explicit sync intent (a mount effect, a sync button) should always
      // reach the network - only a genuinely concurrent duplicate call
      // gets deduped/joined, unlike loadMessages below which deliberately
      // leans on the client's default staleTime to coalesce a burst of
      // near-simultaneous triggers.
      staleTime: 0,
    });
    if (syncChannelsGenRef.current !== gen) {
      appLog('[Sync] syncChannels(): stale response (a newer sync started since) - discarding');
      return;
    }
    if (page && page.channels.length > 0) {
      // Re-merges against the FRESHEST channels state (via the functional
      // updater), not the `channels` closure snapshot fetchChannels(channels)
      // above was called with - that snapshot is taken synchronously before
      // the network round trip, so it's already stale by the time this
      // resolves if anything else (most commonly: this exact syncChannels
      // call was itself triggered by the server's own "cl" broadcast echo
      // of a follow/unfollow the user just pressed - see stats/
      // channel_follow.wsl's BroadcastFollowToUser) touched following/
      // liked/muted in between. GET /channels never carries those three
      // per-account fields at all (see normalizeChannel's existing?.x??false
      // fallback chain) - they only ever come from a local optimistic
      // update or the separate GET /stats/channel/{nickname}/me - so this
      // sync must never let a stale merge snapshot regress them behind
      // whatever's actually current, no matter which order the HTTP
      // response and the Cue "cl" event happen to arrive in.
      setChannels((prev) => {
        const prevById = new Map(prev.map((c) => [c.id, c]));
        return page.channels.map((c) => {
          const latest = prevById.get(c.id);
          return latest ? { ...c, following: latest.following, liked: latest.liked, muted: latest.muted } : c;
        });
      });
      setChannelsHasMore(page.pagination.hasMore);
      appLog(`[Sync] syncChannels(): applied ${page.channels.length} channel(s)`);
      void refreshUnread();
    } else if (page) {
      // Authenticated + successful + genuinely zero channels (GET /channels
      // is a plain SMEMBERS user:channels:<userId>) - the user really has
      // none. Clear any stale cache instead of keeping it forever: a
      // phantom local-only "New Channel" from a failed create would
      // otherwise linger in the list, get auto-joined over Cue (403), and
      // - before isChannelOwner stopped trusting an empty ownerIds - show
      // an Edit button to whoever was signed in. Distinct from page ===
      // null (fetch failed / offline), which still leaves the cache alone.
      setChannels([]);
      setChannelsHasMore(false);
      appLog('[Sync] syncChannels(): server returned zero channels, cleared local cache');
    } else {
      appLog(`[Sync] syncChannels(): fetch failed, leaving existing ${channels.length} channel(s) untouched`);
    }
  };
  const syncTemplates = async () => {
    appLog('[Sync] syncTemplates(): starting');
    const page = await queryClient.query({
      queryKey: queryKeys.templates(0),
      queryFn: () => fetchTemplates(templates),
      staleTime: 0,
    });
    if (page && page.templates.length > 0) {
      setTemplates(page.templates);
      setTemplatesHasMore(page.pagination.hasMore);
      appLog(`[Sync] syncTemplates(): applied ${page.templates.length} template(s)`);
    } else {
      appLog(`[Sync] syncTemplates(): empty/null result (${page === null ? 'fetch failed' : 'no templates'}), leaving existing ${templates.length} template(s) untouched`);
    }
  };
  // Mirrors syncTemplates - refetch page 1 and replace. GET /mappings is
  // owner-scoped, so an empty result for a fresh account is legitimate;
  // only a null (fetch failed) leaves the existing list untouched.
  const syncMappings = async () => {
    const page = await queryClient.query({
      queryKey: queryKeys.mappings(0),
      queryFn: () => fetchMappings(mappings),
      staleTime: 0,
    });
    if (page) {
      setMappings(page.mappings);
      setMappingsHasMore(page.pagination.hasMore);
    }
  };

  // GET /channels/invites -> nicknames, then hydrate each into a
  // ChannelInvite (channel + inviter name). Same "list ids then fetch
  // each" shape as syncChannels. A failed list leaves the current invites
  // untouched; an authenticated empty list clears them.
  const syncChannelInvites = async () => {
    const nicknames = await queryClient.query({
      queryKey: queryKeys.channelInvites(),
      queryFn: () => fetchChannelInvites(),
      staleTime: 0,
    });
    if (!Array.isArray(nicknames)) return;
    const hydrated = await Promise.all(
      nicknames.map(async (nickname): Promise<ChannelInvite | null> => {
        const [channel, member] = await Promise.all([
          fetchChannel(nickname),
          profile.userId ? fetchChannelMember(nickname, profile.userId) : Promise.resolve(null),
        ]);
        if (!channel) return null;
        let invitedByName = '';
        let invitedByNickname = '';
        if (member?.invitedBy) {
          const inviter = await fetchUserProfileById(member.invitedBy).catch(() => null);
          if (inviter) {
            invitedByName = inviter.name.fullname || inviter.name.name || '';
            invitedByNickname = inviter.nickname ?? '';
          }
        }
        return { channel, invitedByName, invitedByNickname };
      }),
    );
    setChannelInvites(hydrated.filter((i): i is ChannelInvite => i !== null));
  };

  const acceptInvite = async (channelNickname: string) => {
    const invite = channelInvites.find((i) => i.channel.nickname === channelNickname);
    await queryClient.query({
      queryKey: queryKeys.channelInviteAccept(channelNickname),
      queryFn: () => acceptChannelInvite(channelNickname, profile.userId),
      retry: 0,
      staleTime: 0,
    });
    setChannelInvites((prev) => prev.filter((i) => i.channel.nickname !== channelNickname));
    if (invite) addChannelToPersonalList(invite.channel);
    await syncChannels();
  };

  const declineInvite = async (channelNickname: string) => {
    // Direct call, not through queryClient.query - removeChannelMember
    // resolves to void, and query-core rejects an undefined queryFn result.
    await removeChannelMember(channelNickname, profile.userId);
    setChannelInvites((prev) => prev.filter((i) => i.channel.nickname !== channelNickname));
  };

  // Fetches the NEXT page (offset = however many are already loaded) and
  // appends, unlike syncChannels/syncTemplates above which always replace
  // with a fresh page 1. Merges by id (a Map, same pattern loadMessages
  // uses below) rather than a naive concat, since a page boundary can
  // shift between calls if something was created/deleted concurrently.
  const loadMoreChannels = async () => {
    if (!channelsHasMore) return;
    const offset = channels.length;
    const page = await queryClient.query({
      queryKey: queryKeys.channels(offset),
      queryFn: () => fetchChannels(channels, offset),
      staleTime: 0,
    });
    if (!page) return;
    setChannelsHasMore(page.pagination.hasMore);
    if (page.channels.length === 0) return;
    setChannels((prev) => {
      const byId = new Map(prev.map((c) => [c.id, c] as const));
      for (const c of page.channels) byId.set(c.id, c);
      return [...byId.values()];
    });
  };

  const loadMoreTemplates = async () => {
    if (!templatesHasMore) return;
    const offset = templates.length;
    const page = await queryClient.query({
      queryKey: queryKeys.templates(offset),
      queryFn: () => fetchTemplates(templates, offset),
      staleTime: 0,
    });
    if (!page) return;
    setTemplatesHasMore(page.pagination.hasMore);
    if (page.templates.length === 0) return;
    setTemplates((prev) => {
      const byId = new Map(prev.map((t) => [t.id, t] as const));
      for (const t of page.templates) byId.set(t.id, t);
      return [...byId.values()];
    });
  };

  const loadMoreMappings = async () => {
    if (!mappingsHasMore) return;
    const offset = mappings?.length;
    const page = await queryClient.query({
      queryKey: queryKeys.mappings(offset),
      queryFn: () => fetchMappings(mappings, offset),
      staleTime: 0,
    });
    if (!page) return;
    setMappingsHasMore(page.pagination.hasMore);
    if (page.mappings?.length === 0) return;
    setMappings((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m] as const));
      for (const m of page.mappings) byId.set(m.id, m);
      return [...byId.values()];
    });
  };

  useEffect(() => {
    // Manual-refresh mode (Settings > Connection): show the cached lists
    // and wait for the user to pull fresh data themselves.
    if (readLS(LS.manualRefresh, 'false') === 'true') return;
    syncTemplates();
    // GET /channels and /mappings are the caller's own lists and need a
    // session; firing them at boot made every logged-out public page load
    // (e.g. /public/@nick) hit a guaranteed 4xx. On those routes the
    // isLoggedIn effect below runs them once a session is confirmed (so a
    // signed-in visitor still gets their lists).
    if (isPublicBootPath(window.location.pathname)) return;
    syncChannels();
    syncMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirrors the current channel/template lists into localStorage on every
  // change (fetch, create, update, delete, or any local-only mutation like
  // an appended chat message) so the cache always reflects what's on
  // screen, not just the last fetch.
  // Debounced. Message history is NOT part of this cache any more - it lives
  // in IndexedDB (signalDb.ts) and is seeded back below.
  useEffect(() => {
    const t = setTimeout(
      () => saveCache(LS.channelsCache, channels),
      CACHE_WRITE_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [channels]);

  // Seeds each channel's newest cached messages from IndexedDB, so a reload
  // (or being offline) shows the last-known history immediately instead of
  // waiting for the network. Only fills a feed that is still empty, so it can
  // never overwrite fresher live data.
  const seededChannelsRef = useRef(new Set<string>());
  useEffect(() => {
    for (const c of channels ?? []) {
      if (seededChannelsRef.current.has(c.id)) continue;
      seededChannelsRef.current.add(c.id);
      void signalDb.loadNewest(c.id, SEED_MESSAGES_PER_CHANNEL).then((cached) => {
        if (cached.length > 0 && messageStore.messages(c.id).length === 0) messageStore.setMessages(c.id, cached);
      });
    }
  }, [channels]);

  useEffect(() => {
    const t = setTimeout(() => saveCache(LS.templatesCache, templates), CACHE_WRITE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [templates]);

  useEffect(() => {
    const t = setTimeout(() => saveCache(LS.mappingsCache, mappings), CACHE_WRITE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [mappings]);

  // The session lives in an httpOnly zmist_session cookie now (see
  // dataApi.ts's header comment), invisible to JS by design - so unlike
  // the old localStorage-token check, this can't be known synchronously
  // at mount. isLoggedIn starts false and authChecked starts false; a
  // boot-time effect below asks the backend "am I logged in" (a cheap
  // authenticated GET) and sets both once it resolves. App.tsx's
  // RequireAuth/RequireGuest gate on authChecked so a real logged-in
  // session doesn't flash a guest screen (or redirect away from one)
  // before that first check lands.
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // The boot-time "am I logged in" check - see the state declarations'
  // comment above. Reuses fetchSecurity() (GET /security) as the probe
  // rather than adding a dedicated endpoint just for this: it's already
  // the cheapest authenticated GET the app needs regardless, so a real
  // session gets its security credentials populated in the same round
  // trip instead of needing a second one right after (the other
  // fetchSecurity effect below still re-fires once isLoggedIn flips true,
  // a harmless redundant call on first load, not worth restructuring
  // around).
  useEffect(() => {
    let cancelled = false;
    // Routes through queryClient.query with the SAME key
    // (queryKeys.security()) as the other fetchSecurity effect below - the
    // comment there already called out that effect firing again once
    // isLoggedIn flips true (right after this one resolves) as "a harmless
    // redundant call... not worth restructuring around". The client's
    // default staleTime (see lib/queryClient.ts) now actually coalesces
    // that second call into this one's cached result instead of hitting
    // the network twice, without either effect needing to know about the
    // other.
    queryClient.query({ queryKey: queryKeys.security(), queryFn: fetchSecurity }).then((remote) => {
      if (cancelled) return;
      if (remote) {
        setSecurity(remote);
        setIsLoggedIn(true);
      }
      setAuthChecked(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unread counts need auth; syncChannels' own refreshUnread call can race
  // ahead of isLoggedIn flipping true on a cold reload with a valid
  // session cookie. This backstop re-pulls once login is confirmed.
  useEffect(() => {
    if (isLoggedIn) void refreshUnread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Pull the channel + template lists as soon as a login is confirmed -
  // the boot-time sync above runs once at app load, before the session
  // cookie exists on a first-ever login, so without this the lists stayed
  // empty until a manual refresh or a full reload. Skipped in manual-
  // refresh mode (Settings > Connection).
  useEffect(() => {
    if (!isLoggedIn || manualRefreshRef.current) return;
    void syncChannels();
    void syncTemplates();
    void syncMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Poll running/queued AI report jobs so their status advances even when
  // the live Cue "rj" relay never arrives (a flaky connection, or the
  // Reports panel opened onto jobs that were already running). One
  // persistent 5s interval for the provider's lifetime; it only touches
  // the network for channels that actually have an active job loaded, and
  // does nothing at all in manual-refresh mode.
  useEffect(() => {
    const id = setInterval(() => {
      if (manualRefreshRef.current) return;
      for (const [channelId, list] of Object.entries(reportJobsRef.current)) {
        if (list.some((j) => (REPORT_JOB_ACTIVE as ReportJobStatus[]).includes(j.status))) {
          void loadReportJobs(channelId);
        }
      }
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedTab, setSelectedTab] = useState<StoreState['selectedTab']>('channels');
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => (readLS(LS.themeMode, 'system') as ThemeMode) || 'system');
  const [language, setLanguageState] = useState<AppLanguage>(() => (readLS(LS.language, 'english') as AppLanguage) || 'english');
  // Defaults to on (three columns), matching ChatScreen's existing
  // xl:grid-cols-3 behavior before this preference existed - only an
  // explicit "false" written by setThreeColumnLayout below turns it off.
  const [threeColumnLayout, setThreeColumnLayoutState] = useState<boolean>(
    () => readLS(LS.threeColumnLayout, 'true') !== 'false'
  );
  // Defaults to off - Diagnostics is an opt-in tool; an explicit "true"
  // written by setDiagnosticsMenuEnabled below adds its main-nav tab.
  const [diagnosticsMenuEnabled, setDiagnosticsMenuEnabledState] = useState<boolean>(
    () => readLS(LS.diagnosticsMenuEnabled, 'false') === 'true'
  );
  // Defaults to off (automatic refresh on) - only an explicit "true"
  // written by setManualRefresh below turns the automatic fetches off.
  const [manualRefresh, setManualRefreshState] = useState<boolean>(
    () => readLS(LS.manualRefresh, 'false') === 'true'
  );
  // Read by effects/closures that fire outside React's render pass (the
  // report-job poll interval especially) so they always see the current
  // choice without being torn down and rebuilt when it changes.
  const manualRefreshRef = useRef(manualRefresh);
  manualRefreshRef.current = manualRefresh;
  const [profile, setProfileState] = useState<ProfileDraft>(loadProfile);
  // Same "stable-closure relay handler" reasoning as reportJobsRef below -
  // messageFromSignal's isMine check reads this. The persistent Cue relay
  // handler (registered once, see cueRelayHandlerRef) is set up as soon as
  // security credentials are ready, which can race ahead of the GET
  // /profile fetch that fills in the real nickname - reading `profile`
  // directly there would permanently freeze isMine against whatever
  // nickname (often still "") existed at that first registration, for
  // every relay-driven refetch for the rest of the session. That's what
  // made a sender's OWN messages render on the left instead of the right
  // after any relay-triggered reload (a signal send included, once
  // patchChannel's message-patch sync noise - see its own comment - was
  // no longer the dominant cause of those reloads).
  const profileRef = useRef(profile);
  profileRef.current = profile;
  const [security, setSecurityState] = useState<SecurityDraft>(() => ({
    key: readLS(LS.securityKey, defaultSecurity.key),
    secret: readLS(LS.securitySecret, defaultSecurity.secret),
  }));
  const [aiSettings, setAiSettingsState] = useState<AISettingsDraft>(readAiSettings);
  // AI report jobs, keyed by channel id. Seeded by loadReportJobs when the
  // Reports screen opens, then patched live from the Cue "rj" relay. The
  // ref mirrors the state so the persistent (stable-closure) relay handler
  // can read current jobs without being re-registered.
  const [reportJobs, setReportJobs] = useState<Record<string, ReportJob[]>>({});
  const [reportJobsMeta, setReportJobsMeta] = useState<
    Record<string, { total: number; hasMore: boolean; loadingMore: boolean }>
  >({});
  const reportJobsRef = useRef<Record<string, ReportJob[]>>({});
  reportJobsRef.current = reportJobs;
  const [tierCatalog, setTierCatalog] = useState<TierCatalogEntry[]>([]);
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // See StoreState's isSyncing comment - counts in-flight queries across
  // the 'channels'/'templates'/'channelMessages' key families (see
  // lib/queryClient.ts's queryKeys), i.e. exactly the calls this file
  // routes through queryClient.query.
  const isSyncingChannels = useIsFetching({ queryKey: ['channels'] }) > 0;
  const isSyncingTemplates = useIsFetching({ queryKey: ['templates'] }) > 0;
  const isSyncingMappings = useIsFetching({ queryKey: ['mappings'] }) > 0;
  const isSyncingMessages = useIsFetching({ queryKey: ['channelMessages'] }) > 0;
  const isSyncing = isSyncingChannels || isSyncingTemplates || isSyncingMappings || isSyncingMessages;

  // Best-effort: pull the server's copy of Settings so they persist across
  // devices/reloads, not just localStorage. GET /profile requires a session
  // (fetchProfile sends the cookie), and there's nothing to fetch if we're
  // logged out or never recorded which email we're logged in as. `force`
  // bypasses the query cache's staleTime - used by the Cue "pu" relay,
  // where an admin has just changed this account's tier / flags / limits
  // server-side and we need the new values now, not a cached copy.
  const syncSettingsFromServer = (opts?: { force?: boolean }) => {
    if (!isLoggedIn) return;
    const email = readLS(LS.email);
    if (!email) return;
    queryClient
      .query({
        queryKey: queryKeys.profile(email),
        queryFn: () => fetchProfile(email),
        ...(opts?.force ? { staleTime: 0 } : {}),
      })
      .then((remote) => {
        if (!remote) return;
        if (Object.values(remote.profile).some((v) => v)) {
          setProfileState((prev) => ({ ...prev, ...remote.profile }));
        }
        if (remote.themeMode) setThemeModeState(remote.themeMode);
        if (remote.language) setLanguageState(remote.language);
      });
    if (opts?.force) {
      // A tier change (the most common "pu" cause) also moves the fuller
      // billing state the Plan screen / upgrade gating read.
      queryClient
        .query({ queryKey: queryKeys.billing(), queryFn: fetchBillingState, staleTime: 0 })
        .then((state) => {
          if (state) {
            setBilling(state);
            if (state.tier?.id) setProfileState((prev) => ({ ...prev, tier: state.tier.id }));
          }
        })
        .catch(() => {});
    }
  };

  useEffect(() => {
    syncSettingsFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Pending channel invites - re-pulled whenever the session or the
  // resolved userId changes (the userId lands a beat after isLoggedIn, and
  // it's needed to resolve the inviter's name).
  useEffect(() => {
    if (!isLoggedIn) return;
    syncChannelInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, profile.userId]);

  // Best-effort: the public pricing-tier catalog (no auth) plus this
  // account's live billing state (plan + usage + limits) once logged in.
  // Both feed the Plan screen and the "Upgrade" gating in AppLayout; a
  // failure here just leaves profile.tier ('free' default, or whatever
  // GET /profile returned) as the only signal.
  useEffect(() => {
    queryClient
      .query({ queryKey: queryKeys.tierCatalog(), queryFn: fetchTierCatalog })
      .then((tiers) => {
        if (Array.isArray(tiers)) setTierCatalog(tiers);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    queryClient
      .query({ queryKey: queryKeys.billing(), queryFn: fetchBillingState })
      .then((state) => {
        if (state) {
          setBilling(state);
          if (state.tier?.id) setProfileState((prev) => ({ ...prev, tier: state.tier.id }));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Best-effort: pull this account's real Cue signaling credentials
  // (GET /security) so live messaging (see sendMessage/subscribeToChannel
  // below) authenticates as this account rather than staying on the
  // hardcoded local placeholder in defaultSecurity, which the backend has
  // never heard of - every Cue request under it would fail with "unknown
  // security key".
  useEffect(() => {
    if (!isLoggedIn) return;
    // See the boot-time fetchSecurity effect's comment above - same
    // queryKeys.security() key, so this coalesces with that call instead
    // of firing a second GET /security.
    queryClient.query({ queryKey: queryKeys.security(), queryFn: fetchSecurity }).then((remote) => {
      if (remote) setSecurity(remote);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const dark =
        themeMode === 'dark' || (themeMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
      root.classList.toggle('dark', dark);
    };
    apply();
    if (themeMode === 'system') {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.addEventListener('change', apply);
      return () => mql.removeEventListener('change', apply);
    }
  }, [themeMode]);

  // No token to store any more - the backend sets the httpOnly
  // zmist_session cookie in this same response (see dataApi.ts's header
  // comment), so the browser already has it by the time this resolves.
  // result.token is still checked for presence (confirms the backend
  // actually succeeded) but its value itself is never used client-side.
  const login = async (email: string, password: string): Promise<TwoFactorChallenge | void> => {
    if (!email || !password) throw new Error('Email and password are required.');
    const result = await authenticateUser(email, password);
    if (result.success && result.twoFactorRequired && result.challengeId) {
      // Password was accepted but the account has email 2FA on - no
      // session yet. Hand the challenge back to LoginScreen, which
      // collects the emailed code and calls verifyTwoFactor below.
      localStorage.setItem(LS.email, email);
      return { challengeId: result.challengeId, email, kind: 'email' };
    }
    if (result.success && result.authenticatorRequired && result.challengeId) {
      // Same idea, but the account uses an authenticator app instead -
      // LoginScreen collects a code from it (or a backup code) and calls
      // verifyAuthenticator below.
      localStorage.setItem(LS.email, email);
      return { challengeId: result.challengeId, email, kind: 'authenticator' };
    }
    if (!result.success || !result.token) throw new Error(result.error || 'Unable to log in.');
    localStorage.setItem(LS.email, email);
    setIsLoggedIn(true);
  };

  // Second step of an email-2FA login - the verify response carries the
  // same session Set-Cookie a normal login does (see login() above on why
  // the token value itself is never used client-side).
  const verifyTwoFactor = async (challengeId: string, code: string) => {
    const result = await verifyLoginCode(challengeId, code);
    if (!result.success || !result.token) throw new Error(result.error || 'That code was not accepted.');
    setIsLoggedIn(true);
  };

  // Second step of an authenticator-app login - same shape as
  // verifyTwoFactor above, backed by POST /login/verify-authenticator.
  const verifyAuthenticator = async (challengeId: string, code: string) => {
    const result = await verifyAuthenticatorCode(challengeId, code);
    if (!result.success || !result.token) throw new Error(result.error || 'That code was not accepted.');
    setIsLoggedIn(true);
  };

  // Usernameless passkey sign-in: the OS prompt picks the credential, the
  // backend sets the session cookie, and the response names the account.
  // A dismissed prompt is not an error worth a toast - it resolves quietly.
  const loginWithPasskey = async () => {
    const result = await signInWithPasskey();
    if (!result.ok) {
      if (result.cancelled) return;
      throw new Error(result.error);
    }
    if (result.data.email) localStorage.setItem(LS.email, result.data.email);
    setIsLoggedIn(true);
  };

  // Same idea as login() above, but the email is already in hand (see
  // OAuthCallbackScreen.tsx) rather than coming from a POST /login call
  // this function makes itself - social sign-in resolves via POST
  // /oauth/exchange instead, which needs its own screen to read the
  // provider's redirect (?code=&state=), not a plain email/password form.
  // No token parameter any more either, for the same reason as login()
  // above - by the time OAuthCallbackScreen calls this, the session
  // cookie is already set.
  const loginWithToken = (email: string) => {
    localStorage.setItem(LS.email, email);
    setIsLoggedIn(true);
  };

  // `beforeEnter` runs once the account (and its session cookie) exist but
  // before the app flips to logged-in, so a caller can finish a step that
  // needs the new session while its own screen is still mounted.
  const register = async (
    name: string,
    email: string,
    password: string,
    challengeId: string,
    code: string,
    beforeEnter?: () => Promise<void>,
  ) => {
    if (!name || !email || !password) throw new Error('All fields are required.');
    if (!challengeId || !code) throw new Error('Enter the verification code from your email.');
    const nickname = `@${name.toLowerCase().replace(/\s+/g, '')}`;
    const result = await registerUser(name, nickname, email, password, challengeId, code);
    if (!result.success || !result.token) throw new Error(result.error || 'Unable to create an account.');
    localStorage.setItem(LS.email, email);
    const nextProfile = { ...profile, name, nickname };
    setProfileState(nextProfile);
    try {
      localStorage.setItem(LS.profile, JSON.stringify(nextProfile));
    } catch {
      // ignore
    }
    if (beforeEnter) await beforeEnter();
    setIsLoggedIn(true);
  };

  // Passkey sign-up: same email-verified account creation as register(), but
  // the user never picks a password - the account gets a random one nobody
  // sees (they can always set one later via "Forgot your password?") and a
  // passkey is created on this device right after. If the passkey step is
  // dismissed or fails the account still exists and they are signed in, so
  // that is reported as a notice instead of failing the sign-up.
  const registerWithPasskey = async (name: string, email: string, challengeId: string, code: string) => {
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    const password = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    await register(name, email, password, challengeId, code, async () => {
      const added = await registerPasskey(defaultPasskeyLabel());
      if (!added.ok) {
        setErrorMessage(
          `Your account was created, but the passkey was not saved (${added.error}) Add one later in Settings > Security.`,
        );
      }
    });
  };

  const sendRegisterCode = (email: string, turnstileToken: string) => apiSendRegisterCode(email, turnstileToken);
  const resendRegisterCode = (challengeId: string) => apiResendRegisterCode(challengeId);
  const sendPasswordReset = (email: string) => apiSendPasswordReset(email);
  const resetPasswordWithCode = (challengeId: string, code: string, password: string, confirm: string) =>
    apiResetPasswordWithCode(challengeId, code, password, confirm);

  // Calls the backend so it actually clears the httpOnly cookies (client
  // JS can't clear an httpOnly cookie itself - that's the whole point of
  // it being httpOnly) - fire-and-forget is fine here, local state is
  // cleared regardless of whether the network call succeeds, matching
  // this function's pre-existing "always succeeds locally" behavior.
  //
  // Used to only flip isLoggedIn and drop the cached email - every other
  // piece of this account's state (channels, templates, profile,
  // security credentials, AI settings, the localStorage caches
  // backing all of those) stayed in memory/storage untouched. On a shared
  // device, or just logging into a second account in the same browser,
  // the next login briefly (or permanently, for anything an empty-result
  // fetch doesn't overwrite - see fetchChannels/fetchTemplates' own
  // "only a non-empty result overwrites" comments) showed the PREVIOUS
  // account's data. Now resets every piece of account-scoped state back
  // to its pre-login default and clears every localStorage key that
  // shadows it - mirrors iOS's identical ZmistStore.logout() fix.
  // themeMode/language/threeColumnLayout are deliberately left alone -
  // device display preferences, not account data.
  const logout = () => {
    setIsLoggedIn(false);
    setChannels([]);
    setTemplates([]);
    setMappings([]);
    setChannelInvites([]);
    setChannelPins({});
    setChannelOpenedAt({});
    setUnread({ byChannel: {}, total: 0, pins: {}, opens: {} });
    setViewedChannels({});
    viewedChannelFetchesRef.current.clear();
    setChannelsHasMore(true);
    setTemplatesHasMore(true);
    setMappingsHasMore(true);
    messageStore.clear();
    void signalDb.clearAll();
    setProfileState(defaultProfile);
    setSecurityState(defaultSecurity);
    setAiSettingsState(defaultAiSettings);
    setBilling(null);
    queryClient.removeQueries({ queryKey: queryKeys.billing() });
    setSearchQuery('');
    setErrorMessage(null);
    // Drops the live Cue connection tied to the outgoing account's
    // credentials outright, rather than waiting for the next send/
    // subscribe to lazily notice security.key/secret changed (see
    // currentCueClient's own comment on that lazy-rebuild check) - a
    // stale open socket has no reason to stay connected once its account
    // has logged out.
    cueClientRef.current?.disconnect();
    cueClientRef.current = null;
    cueClientCredentialsRef.current = null;
    joinedAllChannelsRef.current = new Set();
    joinedUserRoomRef.current = null;
    cueRelayHandlerRef.current = null;
    localStorage.removeItem(LS.email);
    localStorage.removeItem(LS.profile);
    localStorage.removeItem(LS.securityKey);
    localStorage.removeItem(LS.securitySecret);
    localStorage.removeItem(LS.aiProvider);
    localStorage.removeItem(LS.aiDeepseekKeyLegacy);
    for (const p of AI_PROVIDERS) localStorage.removeItem(LS.aiKeyPrefix + p);
    localStorage.removeItem(LS.channelsCache);
    localStorage.removeItem(LS.templatesCache);
    localStorage.removeItem(LS.mappingsCache);
    apiLogout().catch(() => {});
  };

  // Auto sign-out when the backend rejects an authenticated request with
  // HTTP 401 - the session cookie is gone or expired (see dataApi.ts's
  // reportHttpStatus / the backend "HTTP error status codes" change). Kept
  // as refs so the once-mounted handler always calls the current closures
  // without re-registering. isLoggedInRef guards against a stray 401 on a
  // pre-login public fetch turning into a spurious logout.
  const isLoggedInRef = useRef(isLoggedIn);
  const logoutRef = useRef(logout);
  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
    logoutRef.current = logout;
  });
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!isLoggedInRef.current) return;
      logoutRef.current();
      // logout() clears errorMessage; set it right after so LoginScreen can
      // explain why the user landed back on it.
      setErrorMessage('Your session has expired. Please sign in again.');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const setThemeMode = (m: ThemeMode) => {
    setThemeModeState(m);
    localStorage.setItem(LS.themeMode, m);
  };
  const setLanguage = (l: AppLanguage) => {
    setLanguageState(l);
    localStorage.setItem(LS.language, l);
  };
  const setThreeColumnLayout = (on: boolean) => {
    setThreeColumnLayoutState(on);
    localStorage.setItem(LS.threeColumnLayout, String(on));
  };
  const setDiagnosticsMenuEnabled = (on: boolean) => {
    setDiagnosticsMenuEnabledState(on);
    localStorage.setItem(LS.diagnosticsMenuEnabled, String(on));
  };
  const setManualRefresh = (on: boolean) => {
    setManualRefreshState(on);
    localStorage.setItem(LS.manualRefresh, String(on));
    // Turning automatic refresh back on should feel immediate - pull the
    // lists right now rather than waiting for the next screen visit.
    if (!on) {
      void syncChannels();
      void syncTemplates();
      void syncMappings();
    }
  };
  const setProfile = (p: ProfileDraft) => {
    setProfileState(p);
    try {
      localStorage.setItem(LS.profile, JSON.stringify(p));
    } catch {
      // ignore
    }
  };
  const renameMyNickname = async (newNickname: string) => {
    await apiRenameNickname(newNickname, profile.nickname);
    setProfile({ ...profile, nickname: newNickname });
  };
  const checkUserNicknameAvailable = (nickname: string) => apiCheckUserNicknameAvailable(nickname);

  const setSecurity = (s: SecurityDraft) => {
    setSecurityState(s);
    localStorage.setItem(LS.securityKey, s.key);
    localStorage.setItem(LS.securitySecret, s.secret);
  };
  const setAiSettings = (a: AISettingsDraft) => {
    setAiSettingsState(a);
    localStorage.setItem(LS.aiProvider, a.provider);
    for (const p of AI_PROVIDERS) localStorage.setItem(LS.aiKeyPrefix + p, a.apiKeys[p] ?? '');
  };

  const syncSettings = async (p: ProfileDraft, m: ThemeMode, l: AppLanguage) => {
    await syncProfile(p, m, l);
  };

  const refreshBilling = async () => {
    queryClient.removeQueries({ queryKey: queryKeys.billing() });
    const state = await queryClient
      .query({ queryKey: queryKeys.billing(), queryFn: fetchBillingState })
      .catch(() => null);
    if (state) {
      setBilling(state);
      if (state.tier?.id) setProfileState((prev) => ({ ...prev, tier: state.tier.id }));
    }
  };

  const startUpgrade = async (tierId: AccountTier, interval: BillingInterval = 'monthly') => {
    const url = await apiStartCheckout(tierId, interval);
    window.location.assign(url);
  };

  const openBillingPortalPage = async () => {
    const url = await apiOpenBillingPortal();
    window.open(url, '_blank', 'noopener');
  };

  const channel = (id: string) => channels.find((c) => c.id === id) ?? viewedChannels[id];

  // See StoreContextValue's own comment on this. `id` is really the
  // channel's nickname (see mergeChannel's `id: data.id ?? data.nickname`
  // - the backend never returns a separate id), so fetchChannel(id) is a
  // direct GET /channel/{nickname}, same as any other single-channel
  // fetch.
  const ensureChannelLoaded = (id: string) => {
    if (!id) return;
    if (channels.some((c) => c.id === id)) return;
    if (viewedChannels[id]) return;
    if (viewedChannelFetchesRef.current.has(id)) return;
    viewedChannelFetchesRef.current.add(id);
    fetchChannel(id).then((result) => {
      viewedChannelFetchesRef.current.delete(id);
      if (result) setViewedChannels((prev) => ({ ...prev, [id]: result }));
    });
  };

  // Re-pull one channel's profile/permissions/counts from the backend and
  // merge it into whichever store currently holds it, preserving local-
  // only fields (messages/posts/workflows are re-attached by
  // mergeChannel via the `existing` arg). Driven by the Cue "cu" relay -
  // an edit / permission change / member change on another client (see
  // handleChannelRelay's channelUpdate case). A no-op if we don't hold
  // that channel at all - e.g. a "cu" for a channel we were just removed
  // from, whose GET /channel now 403s; the accompanying "cl" the removed
  // member also gets is what actually drops it from their list.
  const mergeChannelFromServer = async (nickname: string) => {
    if (!nickname) return;
    const existing = channels.find((c) => c.id === nickname) ?? viewedChannels[nickname];
    if (!existing) return;
    const fresh = await fetchChannel(nickname, existing).catch(() => null);
    if (!fresh) return;
    setChannels((prev) =>
      prev.some((c) => c.id === fresh.id)
        ? prev.map((c) =>
            c.id === fresh.id
              ? {
                  ...fresh,
                  posts: c.posts,
                  workflows: c.workflows,
                }
              : c
          )
        : prev
    );
    setViewedChannels((prev) => {
      const current = prev[fresh.id];
      return current
        ? {
            ...prev,
            [fresh.id]: {
              ...fresh,
              // Keep the same local-only fields here as in channels. Public
              // read-only chats can receive the identical cu/loadMessages
              // race through viewedChannels instead.
              posts: current.posts,
              workflows: current.workflows,
            },
          }
        : prev;
    });
  };

  // The Cue "cc" relay - this channel was deleted on another client. Drop
  // it from every local store; a screen still pointed at it falls back to
  // its own "channel not found" empty state (ChatScreen / ChannelDetailsScreen).
  const dropDeletedChannel = (nickname: string) => {
    if (!nickname) return;
    setChannels((prev) => prev.filter((c) => c.id !== nickname));
    setViewedChannels((prev) => {
      if (!prev[nickname]) return prev;
      const next = { ...prev };
      delete next[nickname];
      return next;
    });
    joinedAllChannelsRef.current.delete(nickname);
  };

  // Shared dispatch for the server-originated channel/membership relays
  // ("cu"/"cc"/"cl"/"ci" - see lib/cue/wire.ts). Returns true if it handled
  // the relay (so the caller skips its message-refetch path).
  const handleChannelRelay = (command: string, channelId: string, value: string): boolean => {
    switch (command) {
      case CueCommand.channelUpdate: {
        // "clear_messages" is the one "cu" kind that changes nothing about
        // the channel's own profile/permissions/membership - only its
        // message history - so it needs the message list reloaded, not a
        // channel merge. Every other kind (including an unset/unrecognized
        // one, e.g. an older server mid-deploy) touches the channel's own
        // state, so mergeChannelFromServer is the safe default: it re-pulls
        // the channel doc but explicitly preserves this client's own
        // messages/posts/workflows (see its own comment), so - unlike a
        // plain loadMessages - it can't race or reorder an in-flight
        // optimistic signal send.
        if (parseChannelUpdateKind(value) === ChannelUpdateKind.clearMessages) {
          void loadMessages(channelId, true);
        } else {
          void mergeChannelFromServer(channelId);
        }
        void refreshUnread();
        return true;
      }
      case CueCommand.channelClose:
        dropDeletedChannel(channelId);
        return true;
      case CueCommand.channelList:
        // channelId here is "user:<userId>" - not a real channel; just
        // resync the personal list + unread badges.
        void syncChannels();
        void refreshUnread();
        return true;
      case CueCommand.channelInvites:
        void syncChannelInvites();
        return true;
      case CueCommand.profileUpdate:
        // channelId here is "user:<userId>" - not a real channel. An admin
        // changed this account's tier / feature flags / limits / verified
        // state in ZMAP; re-pull Settings + billing (force past the cache)
        // so it applies without a manual refresh.
        syncSettingsFromServer({ force: true });
        return true;
      default:
        return false;
    }
  };

  // Applies `updater` to the channel with `channelId`, updates local state,
  // and best-effort syncs the result to the backend so profile-field edits
  // survive a reload. Fire-and-forget in the sense that the local update
  // never waits on the network round trip - most callers here are
  // frequent, non-critical local mutations (a new chat message, a
  // like/follow tick) that shouldn't be blocked by a network hiccup, and
  // the backend doesn't store posts/counts anyway (workflows now is a
  // real stored field - see dataApi.ts's mergeChannel). A genuine failure
  // still surfaces via errorMessage
  // (rendered globally in AppLayout.tsx) though - this used to be a
  // silent .catch(() => {}), which was fine for an actual transient
  // network hiccup but meant a real, permanent failure (e.g. a non-owner
  // switching a channel's view/chart type, which channel/update.wsl now
  // rejects with 403 "you do not own this channel") looked like it
  // worked - the local optimistic state updated, nothing ever told the
  // user it didn't actually save, and it would silently diverge from the
  // server forever.
  const patchChannel = (channelId: string, updater: (c: Channel) => Channel, opts?: { sync?: boolean }) => {
    const sync = opts?.sync ?? true;
    let matched = false;
    setChannels((prev) =>
      prev.map((c) => {
        if (c.id !== channelId) return c;
        matched = true;
        const next = updater(c);
        // `channels` used to only ever contain channels this account owns,
        // so every patchChannel caller (view/chart type toggles, message
        // sends, ...) could safely assume a background sync was worth
        // attempting. Now that a followed-but-not-owned public channel can
        // also live here (see addChannelToPersonalList's comment), syncing
        // for a non-owner is a doomed update call - it 403s. upsertChannel
        // no longer masks that behind a create retry (it only falls back to
        // create on a real 404 now), but there's still no point firing a
        // request that can only fail: skip the sync outright for a channel
        // this account doesn't own - the local optimistic update below
        // still applies (so switching tabs etc. still feels instant), it
        // just never tries to persist someone else's channel document.
        //
        // `sync: false` (addMessage/setMessageSyncStatus) is the same
        // "skip it" outcome for a different reason: the backend doesn't
        // store messages as part of the channel doc at all (sending IS
        // the persistence - POST /signal or the Cue "m"/"mt" send), so a
        // message-only patch had nothing worth upserting - it was firing
        // a full POST /channel on every single signal sent, purely to
        // round-trip fields the server was going to ignore anyway (posts
        // still is one of those - workflows no longer is; see
        // channel/create|update.wsl's workflows field). Worse,
        // channel/update.wsl broadcasts a "cu" relay to the channel's own
        // room on every successful save, which the sender is itself
        // joined to - so every signal sent while a chat was open produced
        // an extra forced messages refetch (loadMessages(..., true)) racing
        // the real one, which is what was showing up as the message list
        // visibly reordering ("jumping") right after sending.
        if (sync && isChannelOwner(c)) {
          apiUpsertChannel(next).catch((err) => {
            setErrorMessage(err instanceof Error ? err.message : 'Unable to save that change.');
          });
        }
        return next;
      })
    );
    if (matched) return;
    // Nothing in `channels` matched - most callers get here because the
    // app's initial channel-list sync hasn't completed yet (a real race:
    // see WorkflowScreen.tsx/WorkflowListScreen.tsx's ensureChannelLoaded
    // calls for the fix on that side). Previously this just silently did
    // nothing: `prev.map` found no match, the updater above never ran, and
    // the edit vanished with no error and nothing ever sent to the server
    // either. Fall back to viewedChannels (ensureChannelLoaded's single-
    // channel cache) so the edit still applies and still persists.
    const viewed = viewedChannels[channelId];
    if (!viewed) return;
    const next = updater(viewed);
    setViewedChannels((prev) => ({ ...prev, [channelId]: next }));
    if (sync && isChannelOwner(viewed)) {
      apiUpsertChannel(next).catch((err) => {
        setErrorMessage(err instanceof Error ? err.message : 'Unable to save that change.');
      });
    }
  };

  // ownerIds seeded with profile.userId, not .nickname - purely cosmetic/
  // local, not load-bearing: channel/create.wsl derives the real ownerIds
  // from the token (ignoring this field entirely) and channel/update.wsl
  // always carries forward the EXISTING stored ownerIds regardless of what
  // a request sends (see their own comments). Kept consistent anyway so a
  // locally-held draft never disagrees with what the server will actually
  // store, in case anything ever reads it before the real response merges
  // in below.
  // setChannels (now query-owned - see lib/queryClient.ts's useQueryState)
  // is still what every screen actually renders from; invalidateQueries
  // here is separate from that. It marks the 'channels' query family (a
  // different key namespace than channelsState - see queryKeys' own
  // comment) stale in the query cache so a later queryClient.query call
  // (syncChannels/loadMoreChannels) can't serve a pre-mutation cached page
  // to some other caller.
  const updateChannel = async (updated: Channel) => {
    const saved = await apiUpsertChannel({ ...updated, ownerIds: updated.ownerIds?.length ? updated.ownerIds : [profile.userId] });
    setChannels((prev) => prev.map((c) => (c.id === updated.id ? saved : c)));
    queryClient.invalidateQueries({ queryKey: ['channels'] });
  };

  const createChannel = async (draft: Channel) => {
    const created = await apiCreateChannel({ ...draft, ownerIds: draft.ownerIds?.length ? draft.ownerIds : [profile.userId] });
    setChannels((prev) => [created, ...prev]);
    queryClient.invalidateQueries({ queryKey: ['channels'] });
  };

  const renameChannel = async (id: string, newNickname: string): Promise<Channel> => {
    const existing = channels.find((c) => c.id === id) ?? viewedChannels[id];
    if (!existing) throw new Error('Channel not found.');
    await apiRenameChannel(existing.nickname, newNickname);
    const fresh = await fetchChannel(newNickname, existing);
    const renamed = fresh ?? { ...existing, id: newNickname, nickname: newNickname };
    setChannels((prev) => prev.map((c) => (c.id === id ? renamed : c)));
    setViewedChannels((prev) => {
      const next = { ...prev };
      delete next[id];
      next[newNickname] = renamed;
      return next;
    });
    queryClient.invalidateQueries({ queryKey: ['channels'] });
    return renamed;
  };

  const checkChannelNicknameAvailable = (nickname: string) => apiCheckChannelNicknameAvailable(nickname);

  const deleteChannel = async (id: string) => {
    const existing = channels.find((c) => c.id === id);
    if (!existing) return;
    await apiDeleteChannel(existing.nickname);
    setChannels((prev) => prev.filter((c) => c.id !== id));
    queryClient.invalidateQueries({ queryKey: ['channels'] });
  };

  // Owner "clean chat" - deletes every message in the channel server-side
  // (DELETE /channel/{nickname}/messages, owner only) then empties the
  // channel's local message list so the chat reflects it immediately
  // without a refetch. hasMore is forced false too: there's nothing older
  // left to page back to.
  const clearChannelMessages = async (channelNickname: string): Promise<number> => {
    const { deletedCount } = await apiClearChannelMessages(channelNickname);
    const existing = channels.find((c) => c.nickname === channelNickname);
    if (existing) {
      messageStore.setMessages(existing.id, []);
      messageStore.setHasMore(existing.id, false);
      void signalDb.clearChannel(existing.id);
    }
    return deletedCount;
  };

  // A member removing themselves from a channel ("leave") - the
  // self-removal path of DELETE /channels/{nickname}/member/{userId}
  // (member_remove.wsl), the same teardown the owner's "Remove member"
  // button runs. Used for a real member (invited & accepted, or an
  // approved join request); a plain follower uses unfollow instead - see
  // ChannelDetailsScreen's leave-vs-unfollow split. Drops the channel from
  // the personal list right away rather than waiting for the next
  // syncChannels().
  const leaveChannel = async (channelNickname: string) => {
    await removeChannelMember(channelNickname, profile.userId);
    const existing = channels.find((c) => c.nickname === channelNickname);
    if (existing) removeChannelFromPersonalList(existing);
    setChannelInvites((prev) => prev.filter((i) => i.channel.nickname !== channelNickname));
  };

  // See createChannel - a clone lands the same way (new doc, prepended to
  // the local list, query cache invalidated), it just carries the
  // source's content and picks a free nickname/title (see dataApi's
  // cloneChannel).
  const cloneChannel = async (source: Channel, clickSource?: string) => {
    const created = await apiCloneChannel(source, clickSource);
    setChannels((prev) => [created, ...prev]);
    queryClient.invalidateQueries({ queryKey: ['channels'] });
    return created;
  };

  // Stamped here on both paths, not just addTemplate below: upsertTemplate
  // falls back to a create call under the hood whenever the target doesn't
  // exist server-side yet, so this same fallback path can also be the one
  // that first assigns an owner.
  // See updateChannel's identical comment on why invalidateQueries here
  // is about keeping the query cache truthful, not about what renders.
  const updateTemplate = async (updated: Template) => {
    const saved = await apiUpsertTemplate({ ...updated, ownerId: updated.ownerId || profile.nickname });
    setTemplates((prev) => prev.map((t) => (t.id === updated.id ? saved : t)));
    queryClient.invalidateQueries({ queryKey: ['templates'] });
  };

  // A new template belongs to whoever creates it - stamped here (rather than
  // relying on the caller to set it) so every creation path, including the
  // upsert fallback above, ends up with a real ownerId.
  const addTemplate = async (t: Template) => {
    const created = await apiCreateTemplate({ ...t, ownerId: t.ownerId || profile.nickname });
    setTemplates((prev) => [created, ...prev]);
    queryClient.invalidateQueries({ queryKey: ['templates'] });
  };

  const renameTemplate = async (id: string, newNickname: string): Promise<Template> => {
    const existing = templates.find((t) => t.id === id);
    if (!existing) throw new Error('Schema not found.');
    await apiRenameTemplate(existing.nickname, newNickname);
    const fresh = await fetchTemplate(newNickname, existing);
    const renamed = fresh ?? { ...existing, id: newNickname, nickname: newNickname };
    setTemplates((prev) => prev.map((t) => (t.id === id ? renamed : t)));
    queryClient.invalidateQueries({ queryKey: ['templates'] });
    return renamed;
  };

  const checkTemplateNicknameAvailable = (nickname: string) => apiCheckTemplateNicknameAvailable(nickname);

  const deleteTemplate = async (id: string) => {
    const existing = templates.find((t) => t.id === id);
    if (!existing) return;
    await apiDeleteTemplate(existing.nickname);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    queryClient.invalidateQueries({ queryKey: ['templates'] });
  };

  // Integration mappings - mirror the template CRUD above (ownerId stamped
  // here, local list kept in sync, query cache invalidated). addMapping /
  // updateMapping return the saved doc so the manage screen can navigate.
  const addMapping = async (m: Mapping): Promise<Mapping> => {
    const created = await apiCreateMapping({ ...m, ownerId: m.ownerId || profile.nickname });
    setMappings((prev) => [created, ...prev.filter((x) => x.id !== created.id)]);
    queryClient.invalidateQueries({ queryKey: ['mappings'] });
    return created;
  };

  const updateMapping = async (updated: Mapping): Promise<Mapping> => {
    const saved = await apiUpsertMapping({ ...updated, ownerId: updated.ownerId || profile.nickname });
    setMappings((prev) => prev.map((m) => (m.id === updated.id ? saved : m)));
    queryClient.invalidateQueries({ queryKey: ['mappings'] });
    return saved;
  };

  const deleteMapping = async (nickname: string) => {
    await apiDeleteMapping(nickname);
    setMappings((prev) => prev.filter((m) => m.nickname !== nickname));
    queryClient.invalidateQueries({ queryKey: ['mappings'] });
  };

  // Returns the saved mapping, carrying the new raw ingestToken this one
  // time (see Mapping.ingestToken's comment) - the manage screen shows/
  // lets the user copy it, then it's gone from every subsequent fetch.
  const rotateMappingIngestToken = async (nickname: string): Promise<Mapping> => {
    const existing = mappings?.find((m) => m.nickname === nickname);
    const saved = await apiRotateMappingIngestToken(nickname, existing);
    setMappings((prev) => prev.map((m) => (m.nickname === nickname ? saved : m)));
    return saved;
  };

  // Returns the saved mapping with its new webhookSecret - unlike
  // rotateMappingIngestToken above, this ISN'T a one-time reveal (see
  // Mapping.webhookSecret's comment), so it keeps showing up on every
  // later fetch too, not just this response.
  const rotateMappingWebhookSecret = async (nickname: string): Promise<Mapping> => {
    const existing = mappings?.find((m) => m.nickname === nickname);
    const saved = await apiRotateMappingWebhookSecret(nickname, existing);
    setMappings((prev) => prev.map((m) => (m.nickname === nickname ? saved : m)));
    return saved;
  };

  // Pauses/resumes a mapping - the Integrations list's toggle. Returns the
  // saved mapping like the other mapping actions above.
  const setMappingEnabled = async (nickname: string, enabled: boolean): Promise<Mapping> => {
    const existing = mappings?.find((m) => m.nickname === nickname);
    const saved = await apiSetMappingEnabled(nickname, enabled, existing);
    setMappings((prev) => prev.map((m) => (m.nickname === nickname ? saved : m)));
    return saved;
  };

  const isMappingOwner = (m: Mapping) => !m.ownerId || m.ownerId === profile.nickname;

  // See cloneChannel - the template equivalent, mirroring addTemplate's
  // local-list update.
  const cloneTemplate = async (source: Template, clickSource?: string) => {
    const created = await apiCloneTemplate(source, clickSource);
    setTemplates((prev) => [created, ...prev]);
    queryClient.invalidateQueries({ queryKey: ['templates'] });
    return created;
  };

  // "Publish as template" - see dataApi's publishChannelAsTemplate for the
  // full rationale. Same local-list/query-cache update as cloneTemplate.
  const publishChannelAsTemplate = async (source: Channel, signals: TemplateSignal[]) => {
    const created = await apiPublishChannelAsTemplate(source, signals);
    setTemplates((prev) => [created, ...prev]);
    queryClient.invalidateQueries({ queryKey: ['templates'] });
    return created;
  };

  // Empty ownerId means no owner has been recorded for this template yet -
  // treated as ours.
  const isTemplateOwner = (t: Template) => !t.ownerId || t.ownerId === profile.nickname;

  // Gates every owner-only channel action (editing the channel at all,
  // managing members, "Import messages from CSV"). ownerIds is an array of
  // real userIds now (see backend/workflows/channel/create.wsl) - the
  // caller is an owner iff they're in it. Promoting a co-owner adds them
  // to ownerIds server-side (POST /channels/{n}/owners), so this one check
  // covers them too, no separate manageChannel lookup.
  //
  // No "empty ownerIds => treated as mine" fallback: every real channel
  // carries ownerIds after migrate-channel-owner-ids, so an empty list on
  // a channel resolved from the local cache means a stale/phantom entry
  // (e.g. a never-synced local "New Channel" draft) - and treating a
  // phantom as owned by whoever happens to be looking is exactly the bug
  // that let any account see an Edit button for someone else's channel. An
  // unsaved brand-new draft is handled separately by ChannelManageScreen's
  // isNew branch.
  const isChannelOwner = (c: Channel) => !!profile.userId && (c.ownerIds ?? []).includes(profile.userId);

  const updateWorkflow = (updated: Workflow, channelId: string) =>
    patchChannel(channelId, (c) => {
      const exists = c.workflows.some((w) => w.id === updated.id);
      return {
        ...c,
        workflows: exists
          ? c.workflows.map((w) => (w.id === updated.id ? updated : w))
          : [{ ...updated, id: updated.id || uid() }, ...c.workflows],
      };
    });

  // New messages go to the front, not the end - matches iOS's
  // ZmistStore.addMessage (channels[index].messages.insert(message, at: 0)),
  // so a message list rendered in plain array order shows newest at top on
  // both platforms without needing separate scroll-to-position logic.
  const addMessage = (m: Message, channelId: string) =>
    messageStore.prepend(channelId, m);

  // Reused across sends/subscriptions rather than opened per call, mirroring
  // iOS's ZmistStore.currentCueClient() - a WebSocket connection is
  // meaningfully more expensive to open than a REST call, and every send
  // while viewing the same channel should land in the same room. Rebuilt
  // only when the account's real credentials (see the GET /security effect
  // above) actually change.
  const cueClientRef = useRef<CueClient | null>(null);
  const cueClientCredentialsRef = useRef<{ key: string; secret: string } | null>(null);
  // Which channel ids the startup effect below has already joined on the
  // live Cue client, so a later `channels` change only joins the new
  // ones. Cleared on logout (the client itself is torn down there).
  const joinedAllChannelsRef = useRef<Set<string>>(new Set());
  // The personal user room ("user:<userId>") this client has joined, if
  // any - the channel it's keyed to so a userId change (rare) rejoins the
  // right one. Cleared on logout. Server-originated "cl"/"ci" list events
  // are delivered here (see backend/internal/protocol/emit.go).
  const joinedUserRoomRef = useRef<string | null>(null);
  // Unsubscribe for the one persistent relay handler that refetches
  // whatever channel a relay names - registered once per client.
  const cueRelayHandlerRef = useRef<(() => void) | null>(null);
  // The channel currently open on screen (set by subscribeToChannel) - the
  // only one allowed the deep message window - and a mirror of `channels`
  // for the timer-driven idle trim below.
  const activeChannelRef = useRef<string | null>(null);
  const channelsMirrorRef = useRef<Channel[]>([]);
  channelsMirrorRef.current = channels;
  // Per-channel send-spacing state for markChannelViewed's POST /read.
  const markReadRef = useRef(new Map<string, { lastSent: number; timer: ReturnType<typeof setTimeout> | undefined }>());
  // Trailing-throttled unread refresh for inline-signal relays (which skip
  // the message refetch that used to carry it).
  const unreadTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const scheduleUnreadRefresh = () => {
    if (unreadTimerRef.current) return;
    unreadTimerRef.current = setTimeout(() => {
      unreadTimerRef.current = undefined;
      void refreshUnread();
    }, UNREAD_REFRESH_GAP_MS);
  };
  // Per-channel refetch throttle state for the relay handler above.
  const relayThrottleRef = useRef(new Map<string, { pending: boolean; timer: ReturnType<typeof setTimeout> | undefined }>());
  const currentCueClient = (): CueClient | null => {
    if (!security.key || !security.secret) return null;
    // defaultSecurity is a local-only placeholder shown in Settings >
    // Security before the real GET /security fetch (see the effect above)
    // completes - it doesn't correspond to any registered account, so a
    // Cue request under it would always fail server-side. Skip opening a
    // connection at all until real credentials are in place, rather than
    // attempting (and failing) every send/subscribe until then - this also
    // keeps tests (which never log in, so never fetch real credentials)
    // from making real network connections.
    if (security.key === defaultSecurity.key && security.secret === defaultSecurity.secret) return null;
    const current = cueClientCredentialsRef.current;
    if (cueClientRef.current && current?.key === security.key && current?.secret === security.secret) {
      return cueClientRef.current;
    }
    cueClientRef.current?.disconnect();
    const client = new CueClient({ securityKey: security.key, secret: security.secret });
    cueClientRef.current = client;
    cueClientCredentialsRef.current = { key: security.key, secret: security.secret };
    return client;
  };

  const reconnectCue = () => {
    void currentCueClient()?.forceReconnect();
  };

  // Patches one message's syncStatus by id, not the whole channel - used by
  // sendMessage below to report whether the POST /signal persist actually
  // landed, since that used to be a silent `.catch(() => {})` with no way
  // for the UI to tell "saved" from "still trying" from "the server
  // rejected it" (see MessagesScreen's sync icon).
  const setMessageSyncStatus = (channelId: string, messageId: string, syncStatus: Message['syncStatus']) =>
    messageStore.patch(channelId, messageId, (m) => ({ ...m, syncStatus }));

  // Appends the message locally (instant, optimistic), then sends it -
  // over Cue first (WS here, UDP on iOS), REST only as a fallback for
  // when Cue itself can't be reached. Cue is the primary path now: the
  // backend's storage stage (triage_message.wsl -> persist_message.wsl,
  // see that workflow's header comment) persists a "m"/"mt" send the
  // same way REST's POST /signal always has, so a successful Cue ack
  // (202) already means "sent and being durably stored" - REST is no
  // longer run in parallel on every send the way it used to be. See
  // lib/cue/client.ts's sendMessage's own comment for what "durably
  // stored" does and doesn't guarantee about timing (short version:
  // redelivery-safe, not synchronously written the instant the ack
  // arrives, unlike REST's own 201).
  // signalNumber (a TemplateSignal.number) is how a tap on a template
  // signal button is sent - see MessagesScreen.tsx's dispatchSignal. It
  // rides along on both paths: the REST fallback (so the backend resolves
  // type/value from the preset server-side, ignoring the locally-built
  // `type`/`value` above for that purpose - see signal/create.wsl's
  // CheckSignalNumberGiven branch) and the Cue send (the "mt" command,
  // resolved the same way server-side in triage). A plain typed message
  // leaves it undefined and everything behaves exactly as before.
  const sendMessage = (
    text: string,
    channelId: string,
    type = 'message',
    value?: string,
    signalNumber?: string,
    attachment?: Message['attachment'],
    replyToId?: string
  ) => {
    const message: Message = {
      id: uid(),
      sender: profile.nickname,
      senderId: profile.userId,
      text,
      date: new Date().toISOString(),
      isMine: true,
      type: type === 'message' ? undefined : type,
      signalNumber,
      value,
      attachment,
      syncStatus: 'pending',
      replyToId,
    };
    addMessage(message, channelId);

    // A message carrying a file goes REST-only: the Cue "m"/"mt" wire
    // protocol has no attachment field, and persist_message.wsl would
    // store a text-only copy that then blocks the REST write (both are
    // idempotent on message.id - see its CheckExists/RespondAlreadyStored).
    // So POST /signal first (it carries the attachment* fields), then fire
    // a plain Cue send purely as a live "something changed" nudge for
    // other open clients - its own persist just hits RespondAlreadyStored,
    // and the relay makes those clients refetch the full signal (with the
    // attachment) via loadMessages.
    //
    // A report (type: REPORT_TYPE) takes this same REST-first path even
    // with no attachment: the Cue "m" wire protocol has no type field, so
    // a report relayed over Cue is persisted as a plain "message" and
    // loses its report tag on the next reload - and the backend's
    // per-member "send reports" permission (roadmap 4d) is only enforced
    // on POST /signal.
    if (attachment || type === REPORT_TYPE) {
      queryClient
        .query({
          queryKey: ['sendSignal', message.id],
          queryFn: () =>
            apiSendSignal({
              id: message.id,
              channelId,
              type,
              text,
              sender: message.sender,
              date: message.date,
              value: value ?? '',
              signalNumber,
              replyToId,
              ...(attachment
                ? {
                    attachmentKey: attachment.key,
                    attachmentType: attachment.type,
                    attachmentName: attachment.name,
                    attachmentSize: String(attachment.size),
                  }
                : {}),
            }),
          retry: 0,
        })
        .then(() => {
          setMessageSyncStatus(channelId, message.id, undefined);
          currentCueClient()?.sendMessage(message.id, text, channelId).catch(() => {});
        })
        .catch(() => setMessageSyncStatus(channelId, message.id, 'failed'));
      return;
    }

    // Write, not a read - retry: 0 (no auto-retry), same rationale as
    // every other mutation routed through queryClient.query this session
    // (see ChannelDetailsScreen.tsx's toggleLike comment) - a failed send
    // already has its own real recovery path (marks the message 'failed'
    // rather than silently retrying it invisibly), so a hidden network-
    // layer retry would just duplicate/race that. message.id is freshly
    // generated per send (see uid() above), so there's no meaningful
    // dedup case, but this still goes through the shared coordinator for
    // consistency with every other network call.
    const sendViaRest = () =>
      queryClient
        .query({
          queryKey: ['sendSignal', message.id],
          queryFn: () => apiSendSignal({ id: message.id, channelId, type, text, sender: message.sender, date: message.date, value: value ?? '', signalNumber, replyToId }),
          retry: 0,
        })
        .then(() => setMessageSyncStatus(channelId, message.id, undefined))
        .catch(() => setMessageSyncStatus(channelId, message.id, 'failed'));

    // message.id rides onto the wire (lib/cue/wire.ts's withMessageId) so
    // the storage stage persists under this exact id instead of minting
    // a fresh one - see CueClient.sendMessage's own comment. signalNumber
    // takes priority over replyToId (not the other way around): a signal
    // that's also a reply still has to go out as "mt" (carrying replyToId
    // as its own value segment - see buildTemplateSignalValue) so the
    // server resolves and persists its real type/value instead of the
    // "mr" reply path always hardcoding type "message" and dropping which
    // signal it was (see workflows/protocol/triage_message.wsl's
    // EnqueuePersistTemplateSignal vs EnqueuePersistReplyMessage). A reply
    // with no signalNumber (typed text) still goes out as "mr".
    const cueSend = signalNumber
      ? currentCueClient()?.sendTemplateSignal(message.id, signalNumber, channelId, value, undefined, replyToId)
      : replyToId
        ? currentCueClient()?.sendReply(message.id, text, replyToId, channelId)
        : currentCueClient()?.sendMessage(message.id, text, channelId);

    if (!cueSend) {
      // No Cue client at all yet (e.g. security not resolved) - straight
      // to REST, same as Cue being unreachable below.
      void sendViaRest();
      return;
    }
    cueSend
      .then(() => setMessageSyncStatus(channelId, message.id, undefined))
      .catch((error) => {
        // Cue couldn't send it (no ack within its own timeout, a non-202
        // status, or the socket couldn't be opened - see CueClient's own
        // [Cue]-prefixed logging for which one this was) - REST is the
        // fallback specifically for this case, not run proactively
        // alongside every Cue send anymore.
        appWarn(`[Cue] sendMessage(to: ${channelId}) failed, falling back to REST:`, error);
        return sendViaRest();
      });
  };

  // The owner posts a reveal-round control marker (open/reveal/hide/close)
  // - a type "round" message. Deliberately NOT routed through sendMessage:
  // that adds an optimistic copy + patchChannel + a channel upsert, and a
  // recovered write error there could flip the derived phase back and
  // forth. Here it's a plain write-then-refetch: POST /signal (owner-gated
  // server-side), nudge other open clients over Cue, then reload the
  // authoritative list so deriveRound (lib/round.ts) reads server truth.
  // channelId is Channel.id, which on web equals the nickname the /signal
  // endpoint wants.
  const sendRoundAction = async (channelId: string, action: RoundAction) => {
    // Prefer the live path: the Cue "r" command is owner-gated server-side,
    // persists a type "round" marker and relays it to the room so every
    // open client refetches (see triage_message.wsl's HandleRound). REST
    // POST /signal is the fallback - also owner-gated, and it enqueues its
    // own Cue broadcast (signal/create.wsl). Either way, reload the
    // authoritative list afterwards so this client converges.
    const viaRest = () =>
      apiSendSignal({
        id: uid(),
        channelId,
        type: ROUND_TYPE,
        text: roundActionLabel(action),
        sender: profile.nickname,
        date: new Date().toISOString(),
        value: action,
      });
    try {
      const cue = currentCueClient();
      if (cue) await cue.sendRound(action, channelId);
      else await viaRest();
    } catch {
      try {
        await viaRest();
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : 'Unable to update the round.');
      }
    }
    await loadMessages(channelId, true).catch(() => {});
  };

  // Pulls a channel's message history from the backend (the signals the
  // channel's messages are stored as - see dataApi.ts's header comment) and
  // merges it into local state by id, so messages sent from another client
  // (e.g. iOS, which sends over the Cue protocol rather than this REST
  // path) show up here too. Uses setChannels directly rather than
  // patchChannel: this is a read, not an edit, so it shouldn't also trigger
  // a channel upsert back to the backend as a side effect.
  function messageFromSignal(s: FetchedSignal): Message {
    // profileRef, not `profile` directly - see profileRef's own comment.
    // This function is redefined every render like any other closure here,
    // but the ONE render whose version of it actually runs for a given
    // call is whichever render's loadMessages/mergeSignalsIntoChannel got
    // captured by the caller - for the persistent Cue relay handler
    // (cueRelayHandlerRef), that's frozen at first registration, not "now".
    const senderMatchesProfile = s.sender.trim().toLocaleLowerCase() === profileRef.current.nickname.trim().toLocaleLowerCase();
    // Message placement follows the display identity stored with the
    // message. Older messages can carry a senderId from a previous account
    // identity while retaining the same nickname; allowing that stale ID to
    // override the matching nickname makes one user's history split across
    // both sides of the chat. senderId is still retained on Message for
    // server-side ownership operations and diagnostics.
    return {
      id: s.id,
      sender: s.sender,
      senderId: s.senderId,
      text: s.text,
      date: s.date,
      isMine: senderMatchesProfile,
      type: !s.type || s.type === 'message' ? undefined : s.type,
      signalNumber: s.signalNumber,
      value: s.value,
      calc: s.calc,
      labels: s.labels,
      at: s.at,
      attachment: s.attachment,
      additionalChannelIds: s.additionalChannelIds,
      confidence: s.confidence,
      confidenceFactors: s.confidenceFactors,
      number: s.number,
      edited: s.edited,
      replyToId: s.replyToId,
    };
  }

  // Always the newest page (offset 0) - both the initial load AND the
  // real-time "something changed, go refetch" poll on every Cue relay
  // (see subscribeToChannel below) call this, so it must stay a plain
  // refresh-the-top of what's cached, not accumulate pages itself. Older
  // pages are loadMoreMessages' job, a separate function below.
  // Merges a fetched page of signals into whichever store currently holds
  // this channel - `channels` for one you own/joined, `viewedChannels`
  // for a public channel opened from Search/Details but not yet followed
  // (see ensureChannelLoaded's comment). A plain `channels`-only
  // setChannels(prev.map(...)) used to silently no-op for the latter, so
  // opening the read-only chat of an unfollowed public channel showed an
  // empty list with a stuck "load more" spinner even though GET /signals
  // returned rows. Same dual-write shape as applyChannelStats below.
  const mergeSignalsIntoChannel = (channelId: string, signals: FetchedSignal[]) => {
    const incoming = signals.map(messageFromSignal);
    messageStore.upsert(channelId, incoming, {
      cap: channelId === activeChannelRef.current ? MAX_MESSAGES_ACTIVE : MAX_MESSAGES_IDLE,
      // Keep local sync state while the authoritative copy catches up. The
      // server response still replaces display fields, but an optimistic
      // message must not lose its pending/failed indicator during a relay
      // refresh that happens before the storage stage finishes.
      resolve: (existing, fetched) =>
        existing?.syncStatus ? { ...fetched, isMine: existing.isMine, syncStatus: existing.syncStatus } : fetched,
    });
    // Persist only channels this account actually holds - a read-only public
    // preview (viewedChannels) is ephemeral.
    if (channelsMirrorRef.current.some((c) => c.id === channelId)) signalDb.saveMessages(channelId, incoming);
  };

  // Live (relay-inline) signals arrive one per relay - collected here and
  // merged once per LIVE_SIGNAL_COALESCE_MS so a busy channel produces a few
  // store updates a second instead of one per signal.
  const mergeSignalsRef = useRef(mergeSignalsIntoChannel);
  mergeSignalsRef.current = mergeSignalsIntoChannel;
  const liveBufferRef = useRef(new Map<string, FetchedSignal[]>());
  const liveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mergeLiveSignal = (channelId: string, signal: FetchedSignal) => {
    const buf = liveBufferRef.current;
    const list = buf.get(channelId);
    if (list) list.push(signal);
    else buf.set(channelId, [signal]);
    if (liveTimerRef.current) return;
    liveTimerRef.current = setTimeout(() => {
      liveTimerRef.current = null;
      const batches = [...liveBufferRef.current.entries()];
      liveBufferRef.current.clear();
      // The relay handler's closure is frozen at first registration; always
      // call the newest mergeSignalsIntoChannel.
      for (const [id, signals] of batches) mergeSignalsRef.current(id, signals);
    }, LIVE_SIGNAL_COALESCE_MS);
  };

  const messageRefreshesRef = useRef(new Map<string, Promise<void>>());

  const loadMessages = async (channelId: string, force = false) => {
    const inFlightRefresh = force ? messageRefreshesRef.current.get(channelId) : undefined;
    if (inFlightRefresh) return inFlightRefresh;

    const refresh = (async () => {
      appLog(`[Sync] loadMessages(${channelId}): GET /signals starting`);
    // Routes through queryClient.query, unlike the direct apiFetchSignals
    // call this replaced - subscribeToChannel's Cue relay handler can call
    // this several times in quick succession while a channel is busy (one
    // "go refetch" per relay event, not per actual new message), and the
    // client's default staleTime (see lib/queryClient.ts) coalesces that
    // burst into far fewer real GET /signals round trips instead of firing
    // one per relay event. `force` (a Cue relay, or right after the owner
    // posts a round marker) bypasses that window so a state change - a
    // reveal-round flip especially - lands without a manual refresh.
      const page = await queryClient.query({
        queryKey: queryKeys.channelMessages(channelId, 0),
        queryFn: () => apiFetchSignals(channelId),
        ...(force ? { staleTime: 0 } : {}),
      });
      appLog(`[Sync] loadMessages(${channelId}): fetched ${page.signals.length} signal(s)`);
      messageStore.setHasMore(channelId, page.pagination.hasMore);
      if (page.signals.length === 0) {
        appLog(`[Sync] loadMessages(${channelId}): empty result, nothing to merge`);
        return;
      }
      mergeSignalsIntoChannel(channelId, page.signals);
    })();

    if (force) {
      messageRefreshesRef.current.set(channelId, refresh);
      try {
        await refresh;
      } finally {
        if (messageRefreshesRef.current.get(channelId) === refresh) messageRefreshesRef.current.delete(channelId);
      }
      return;
    }
    await refresh;
  };

  // Fetches an OLDER page (offset = however many messages are already
  // loaded for this channel) and merges it in - the infinite-scroll
  // counterpart to loadMessages' always-newest-page refresh above.
  const loadMoreMessages = async (channelId: string) => {
    if (!messageStore.hasMore(channelId)) return;
    // channel(), not channels.find() - a public channel opened read-only
    // (not followed) lives in viewedChannels, and using its message count
    // for the offset is what keeps paging correct there too.
    const loaded = messageStore.messages(channelId).length;
    if (loaded === 0) pageRepeatsRef.current.delete(channelId);
    const repeats = pageRepeatsRef.current.get(channelId) ?? 0;
    const offset = loaded + repeats;
    // Routes through queryClient.query so two scroll-listener firings
    // for the SAME offset (the classic infinite-scroll double-fire, if the
    // caller doesn't already debounce it upstream) share one in-flight
    // request instead of issuing two GET /signals calls for the same page.
    // staleTime: 0 - see syncChannels' identical comment for why an
    // explicit "load more" trigger should always reach the network rather
    // than risk serving a coalesced-but-stale page.
    let page;
    try {
      page = await queryClient.query({
        queryKey: queryKeys.channelMessages(channelId, offset),
        queryFn: () => apiFetchSignals(channelId, offset),
        staleTime: 0,
      });
    } catch (err) {
      // Offline / server unreachable: serve the next older page from
      // IndexedDB so scrolling back still works. Only on failure - a healthy
      // fetch stays authoritative, since the cache can have gaps for signals
      // that arrived while this tab was closed.
      const oldest = messageStore.messages(channelId).at(-1)?.date;
      const older = oldest ? await signalDb.loadOlder(channelId, oldest, 100) : [];
      if (older.length === 0) throw err;
      messageStore.upsert(channelId, older);
      return;
    }
    // An empty page ends the history whatever hasMore claims - otherwise the
    // infinite-scroll sentinel re-asks for the same offset forever.
    messageStore.setHasMore(channelId, page.pagination.hasMore && page.signals.length > 0);
    if (page.signals.length === 0) return;
    mergeSignalsIntoChannel(channelId, page.signals);
    const grew = messageStore.messages(channelId).length - loaded;
    if (grew < page.signals.length) pageRepeatsRef.current.set(channelId, repeats + page.signals.length - grew);
  };

  const messagesHasMore = (channelId: string) => messageStore.hasMore(channelId);

  // Joins this channel's Cue room so live relays reach it. The startup
  // effect below already keeps ONE persistent, app-wide relay handler that
  // refetches (via loadMessages) whatever channel a relay names - for any
  // joined room, open or not - so this used to ALSO register its own
  // screen-scoped handler doing the identical "relay for this channel ->
  // loadMessages" work on top of it. That looked harmless (the comment
  // here used to call it a "harmless overlap - loadMessages is deduped by
  // queryClient"), but it wasn't: loadMessages' force path sets
  // staleTime: 0 specifically so a relay-driven refetch isn't coalesced
  // away, which means two handlers firing for the same relay produced two
  // independent forced GET /signals calls racing each other AND racing
  // the backend's async Cue storage pipeline (protocol/persist_message.wsl
  // persists a live "m"/"mt"/"r" signal on a separate stream, not
  // synchronously before the relay goes out) - whichever response landed
  // last could legitimately differ (message present/absent, or the merge
  // sorting equal-timestamp messages in a different relative order), and
  // the resulting flicker was visible as the message list "jumping" every
  // time a signal was sent while the chat was open. Now this only ensures
  // THIS channel is joined (in case it's brand new or the startup join
  // failed); the app-wide handler is solely responsible for refetching.
  // Drops every non-open channel's messages down to its newest page, and
  // read-only viewed channels down to the few most recent - called a while
  // after leaving a channel so a long session doesn't accumulate every
  // channel's scroll-back history. Trimmed channels are flagged "has more"
  // so scrolling back up re-fetches the rest.
  const trimIdleChannels = () => {
    const active = activeChannelRef.current;
    for (const id of messageStore.ids()) {
      if (id === active) continue;
      // Trimmed channels are flagged "has more" so scrolling back re-fetches the rest.
      if (messageStore.cap(id, MAX_MESSAGES_IDLE)) messageStore.setHasMore(id, true);
    }
    setViewedChannels((prev) => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      // Insertion order = oldest first; keep the newest few and the open one.
      const keep = new Set([...ids.slice(-MAX_VIEWED_CHANNELS), ...(active ? [active] : [])]);
      const next: Record<string, Channel> = {};
      for (const id of ids) if (keep.has(id)) next[id] = prev[id];
      else messageStore.delete(id);
      return Object.keys(next).length === ids.length ? prev : next;
    });
  };

  const subscribeToChannel = (channelId: string): (() => void) => {
    activeChannelRef.current = channelId;
    const client = currentCueClient();
    if (client) {
      client.joinChannel(channelId).catch((error) => {
        appWarn(`[Cue] joinChannel(${channelId}) failed:`, error);
      });
    }
    // Leaving the channel (or switching to another) frees the memory the
    // deep window held, after a grace period so quick back-and-forth
    // navigation doesn't thrash.
    return () => {
      if (activeChannelRef.current === channelId) activeChannelRef.current = null;
      setTimeout(trimIdleChannels, IDLE_TRIM_DELAY_MS);
    };
  };

  // ---- AI report jobs ------------------------------------------------

  const patchReportJob = (channelId: string, jobId: string, patch: Partial<ReportJob>) =>
    setReportJobs((prev) => {
      const list = prev[channelId] ?? [];
      const idx = list.findIndex((j) => j.id === jobId);
      if (idx === -1) return prev;
      const next = list.slice();
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, [channelId]: next };
    });

  // A Cue "rj" relay: {id, s: status, m?: messageId, e?: error}. Patch the
  // job in place; a job this client doesn't know (queued from another
  // device) triggers a one-off refetch of the newest page. On "done" also
  // reload the channel so the freshly-posted report shows without waiting
  // for its own "m".
  const applyReportJobRelay = (channelId: string, raw: string) => {
    let evt: { id?: string; s?: ReportJobStatus; m?: string; e?: string };
    try {
      evt = JSON.parse(raw);
    } catch {
      return;
    }
    if (!evt.id || !evt.s) return;
    const known = (reportJobsRef.current[channelId] ?? []).some((j) => j.id === evt.id);
    if (known) {
      const patch: Partial<ReportJob> = { status: evt.s };
      if (evt.m) patch.messageId = evt.m;
      if (evt.e) patch.error = evt.e;
      if (evt.s === 'running') patch.startedAt = Date.now();
      if (evt.s === 'done' || evt.s === 'failed' || evt.s === 'stopped') patch.finishedAt = Date.now();
      patchReportJob(channelId, evt.id, patch);
    } else {
      // A job from elsewhere - pull a page big enough to cover what's
      // already loaded plus the newcomer, and merge newest-first.
      const loaded = reportJobsRef.current[channelId]?.length ?? 0;
      apiFetchReportJobs(channelId, { offset: 0, limit: Math.max(loaded + 1, REPORT_JOBS_PAGE) })
        .then(({ jobs, total }) => {
          setReportJobs((prev) => ({ ...prev, [channelId]: jobs }));
          setReportJobsMeta((prev) => ({
            ...prev,
            [channelId]: { total, hasMore: jobs.length < total, loadingMore: false },
          }));
        })
        .catch(() => {});
    }
    if (evt.s === 'done') loadMessages(channelId, true).catch(() => {});
  };

  const loadReportJobs = async (channelId: string) => {
    try {
      const { jobs, total } = await apiFetchReportJobs(channelId, { offset: 0, limit: REPORT_JOBS_PAGE });
      setReportJobs((prev) => ({ ...prev, [channelId]: jobs }));
      setReportJobsMeta((prev) => ({
        ...prev,
        [channelId]: { total, hasMore: jobs.length < total, loadingMore: false },
      }));
    } catch {
      /* leave whatever's there */
    }
  };

  // Append the next page of older jobs (infinite scroll in the Reports
  // panel). No-op while a page is in flight or nothing more remains.
  const loadMoreReportJobs = async (channelId: string) => {
    const meta = reportJobsMeta[channelId];
    if (meta && (!meta.hasMore || meta.loadingMore)) return;
    const loaded = reportJobsRef.current[channelId]?.length ?? 0;
    setReportJobsMeta((prev) => ({
      ...prev,
      [channelId]: {
        total: prev[channelId]?.total ?? loaded,
        hasMore: prev[channelId]?.hasMore ?? true,
        loadingMore: true,
      },
    }));
    try {
      const { jobs, total } = await apiFetchReportJobs(channelId, {
        offset: loaded,
        limit: REPORT_JOBS_PAGE,
      });
      let mergedLen = loaded;
      setReportJobs((prev) => {
        const existing = prev[channelId] ?? [];
        const seen = new Set(existing.map((j) => j.id));
        const merged = [...existing, ...jobs.filter((j) => !seen.has(j.id))];
        mergedLen = merged.length;
        return { ...prev, [channelId]: merged };
      });
      setReportJobsMeta((prev) => ({
        ...prev,
        // A short page means the server has nothing more, regardless of the
        // total (which can transiently over-count jobs whose hash has TTL'd
        // out from under a lingering ZSET member).
        [channelId]: { total, hasMore: jobs.length >= REPORT_JOBS_PAGE && mergedLen < total, loadingMore: false },
      }));
    } catch {
      setReportJobsMeta((prev) => ({
        ...prev,
        [channelId]: {
          total: prev[channelId]?.total ?? loaded,
          hasMore: prev[channelId]?.hasMore ?? false,
          loadingMore: false,
        },
      }));
    }
  };

  const enqueueReportJob = async (
    channelId: string,
    opts: {
      provider: AIProvider;
      periodDays: AiReportPeriodDays;
      priority: AiJobPriority;
      apiKey?: string;
      additionalChannelIds?: string[];
      mode?: 'questions';
      questions?: string[];
    }
  ): Promise<ReportJob> => {
    const { job } = await apiEnqueueReportJob(channelId, opts);
    setReportJobs((prev) => ({ ...prev, [channelId]: [job, ...(prev[channelId] ?? [])].slice(0, 50) }));
    return job;
  };

  const fetchChannelQuestions = (channelId: string): Promise<string[]> => apiFetchChannelQuestions(channelId);
  const removeReportQuestion = (channelId: string, question: string): Promise<string[]> =>
    apiRemoveReportQuestion(channelId, question);

  const suggestReportQuestions = (
    channelId: string,
    opts: { provider: AIProvider; periodDays: AiReportPeriodDays; apiKey?: string; additionalChannelIds?: string[] }
  ): Promise<string[]> => apiSuggestReportQuestions(channelId, opts);

  const cancelReportJob = async (channelId: string, jobId: string): Promise<void> => {
    const job = await apiCancelReportJob(channelId, jobId);
    // A queued job comes back "stopped"; a running one comes back
    // unchanged and flips via the "rj" relay a moment later.
    patchReportJob(channelId, jobId, { status: job.status, finishedAt: job.finishedAt });
  };

  // Re-pull GET /stats/unread. Best-effort - a failed load leaves the last
  // known badges in place rather than flashing them to zero.
  const refreshUnread = async () => {
    if (!isLoggedIn) return;
    try {
      const counts = await fetchUnreadCounts();
      setUnread(counts);
      // Server truth for the list-ordering inputs. Merge, not replace, for
      // channelOpenedAt so an optimistic local bump that hasn't round-
      // tripped yet isn't lost (take the newer of the two per channel).
      setChannelPins(counts.pins);
      setChannelOpenedAt((prev) => {
        const merged: Record<string, number> = { ...counts.opens };
        for (const [n, t] of Object.entries(prev)) merged[n] = Math.max(merged[n] ?? 0, t);
        return merged;
      });
    } catch {
      /* keep the previous counts */
    }
  };

  // Mirror the cross-channel unread count onto the PWA app-icon badge
  // (installed / iOS-home-screen builds). Best-effort - unsupported
  // browsers just no-op, and the SW does the same on a background push.
  useEffect(() => {
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    try {
      if (!isLoggedIn) nav.clearAppBadge?.();
      else if (unread.total > 0) nav.setAppBadge?.(unread.total);
      else nav.clearAppBadge?.();
    } catch {
      /* badge is a nice-to-have */
    }
  }, [unread.total, isLoggedIn]);

  // The channel is open on screen (or a live message just landed in it
  // while it's open) - tell the backend it's read up to the newest signal
  // and zero its badge locally right away so the UI doesn't wait a round
  // trip. channelId is the nickname on web (Channel.id === nickname).
  const markChannelViewed = (channelId: string) => {
    const nickname = channel(channelId)?.nickname ?? channelId;
    // Opening a channel bumps its recency in the list order (tier 4 of
    // sortChannels) - locally now, and server-side via markChannelRead.
    setChannelOpenedAt((prev) => ({ ...prev, [nickname]: Date.now() }));
    setUnread((prev) => {
      const had = prev.byChannel[nickname] ?? 0;
      if (had === 0) return prev;
      return { ...prev, byChannel: { ...prev.byChannel, [nickname]: 0 }, total: Math.max(0, prev.total - had) };
    });
    // Never one POST /read per message: a streaming channel would fire it
    // continuously. The first call goes out at once (opening the channel);
    // later ones are spaced MARK_READ_MIN_GAP_MS apart with a single trailing
    // call, which marks read up to the newest signal so it covers everything
    // that arrived in between.
    const slot = markReadRef.current.get(nickname) ?? { lastSent: 0, timer: undefined };
    markReadRef.current.set(nickname, slot);
    if (slot.timer) return;
    const send = () => {
      slot.timer = undefined;
      slot.lastSent = Date.now();
      markChannelRead(nickname).catch(() => {
        /* the next refreshUnread will reconcile */
      });
    };
    const wait = slot.lastSent + MARK_READ_MIN_GAP_MS - Date.now();
    if (wait <= 0) send();
    else slot.timer = setTimeout(send, wait);
  };

  // Pin / unpin a channel to the top of the caller's own list (tier 2 of
  // sortChannels, below any admin-priority channels). Optimistic: patch
  // channelPins right away so the row jumps immediately, then POST/DELETE
  // /channels/{n}/pin. channelId is the nickname (Channel.id === nickname).
  const isChannelPinned = (channelId: string) => channelPins[channel(channelId)?.nickname ?? channelId] !== undefined;

  const setChannelPinned = async (channelId: string, pinned: boolean) => {
    const nickname = channel(channelId)?.nickname ?? channelId;
    setChannelPins((prev) => {
      const next = { ...prev };
      if (pinned) {
        const min = Math.min(0, ...Object.values(next));
        next[nickname] = min - 1;
      } else {
        delete next[nickname];
      }
      return next;
    });
    try {
      await (pinned ? apiPinChannel(nickname) : apiUnpinChannel(nickname));
    } catch (err) {
      // Roll back to server truth on failure.
      void refreshUnread();
      setErrorMessage(err instanceof Error ? err.message : 'Unable to update the pin.');
    }
  };

  // Charts and Reports share ONE "additional channels" selection: both fields
  // are written together and either getter reads the same list (the chart's
  // wins; the report's only matters for data saved before they were tied).
  const getChartCompareChannels = (channelId: string) => {
    const c = channel(channelId);
    return c?.chartCompareChannels ?? c?.reportAdditionalChannels ?? [];
  };
  const setChartCompareChannels = (channelId: string, channels: ChannelRef[]) =>
    patchChannel(channelId, (c) => ({ ...c, chartCompareChannels: channels, reportAdditionalChannels: channels }));

  const getReportAdditionalChannels = (channelId: string) => getChartCompareChannels(channelId);
  const setReportAdditionalChannels = (channelId: string, channels: ChannelRef[]) => setChartCompareChannels(channelId, channels);

  // Mirrors the Charts tab's "Filters & settings" (interval, metric,
  // signal/sender filters, ...) to the channel doc - see
  // SignalActivityChart's onSettingsChange (ChartScreen.tsx wires this up,
  // debounced by useAutoSave) and PublicChannelViewScreen.tsx (reads it
  // back as the public chart's initialSettings). Same patchChannel-is-the-
  // sync pattern as chartCompareChannels/reportAdditionalChannels above -
  // no owner-only guard needed here, patchChannel itself already skips the
  // network call for a channel this account doesn't own.
  const setChartSettings = (channelId: string, settings: ChartSettings) =>
    patchChannel(channelId, (c) => ({ ...c, chartSettings: settings as unknown as Record<string, unknown> }));

  // On startup (and whenever the channel list grows), open the live Cue
  // connection and join every channel the user has, so a message in any
  // of them updates live - not just the one currently on screen. One
  // persistent relay handler does the "relay for channel X -> refetch X"
  // work; joinedAllChannelsRef dedupes so a later `channels` change only
  // joins the newcomers. currentCueClient() returns null until the real
  // GET /security credentials are in place, so this is a no-op until then
  // and re-runs (via the security.* deps) once they arrive.
  useEffect(() => {
    if (!isLoggedIn) return;
    const client = currentCueClient();
    if (!client) return;

    if (!cueRelayHandlerRef.current) {
      cueRelayHandlerRef.current = client.addRelayHandler((relayed) => {
        // A report-job status relay is a live-UI signal, not new channel
        // content - patch the job and stop (applyReportJobRelay reloads
        // messages itself on "done").
        if (relayed.command === CueCommand.reportJob) {
          applyReportJobRelay(relayed.channelId, relayed.value);
          return;
        }
        // Server-originated channel / membership / invite events
        // (backend/internal/protocol/emit.go) - a channel edit, a
        // permission change, an invite, a member add/remove on another
        // client. Handled by their own resync paths, not a message refetch.
        if (handleChannelRelay(relayed.command, relayed.channelId, relayed.value)) return;
        // A REST-written signal (import / agent / webhook-style callers)
        // rides inline in the relay - `{"k":"signal","s":{...}}`, see
        // backend signal.BuildSignalRelayValue - so merge it straight from
        // the WebSocket with no GET /signals at all. Only the unread badge
        // still needs a (throttled) round trip. An oversized/absent
        // payload falls through to the refetch path below.
        const inline = parseInlineSignal(relayed.value);
        if (inline) {
          mergeLiveSignal(relayed.channelId, inline);
          // The open channel's unread is zero by definition - only another
          // channel's badge can have changed.
          if (relayed.channelId !== activeChannelRef.current) scheduleUnreadRefresh();
          return;
        }
        // One relay arrives per written signal, so an import/agent burst
        // used to force a full GET /signals per signal (several a second).
        // Leading + trailing throttle per channel: the first relay
        // refetches at once, further ones inside the window collapse into
        // a single trailing refetch, so the last signal is never missed.
        const ch = relayed.channelId;
        const runRefresh = () => {
          loadMessages(ch, true).catch(() => {});
          // A relay for any channel can change an unread badge - the open
          // channel gets re-marked read by ChatScreen, every other one now
          // has one more unread signal.
          if (ch !== activeChannelRef.current) scheduleUnreadRefresh();
        };
        const slot = relayThrottleRef.current.get(ch);
        if (slot) {
          slot.pending = true;
          return;
        }
        const next: { pending: boolean; timer: ReturnType<typeof setTimeout> | undefined } = { pending: false, timer: undefined };
        const tick = () => {
          if (next.pending) {
            next.pending = false;
            runRefresh();
            next.timer = setTimeout(tick, RELAY_REFRESH_WINDOW_MS);
          } else {
            relayThrottleRef.current.delete(ch);
          }
        };
        next.timer = setTimeout(tick, RELAY_REFRESH_WINDOW_MS);
        relayThrottleRef.current.set(ch, next);
        runRefresh();
      });
    }

    const toJoin = channels.filter((c) => !joinedAllChannelsRef.current.has(c.id));
    // The personal user room - joined once per session (keyed by userId so
    // it's rejoined if that ever changes), so "cl"/"ci" list events reach
    // this client even for a channel it isn't a member of.
    const userRoom = profile.userId ? `user:${profile.userId}` : null;
    const joinUserRoom = !!userRoom && joinedUserRoomRef.current !== userRoom;
    if (toJoin.length === 0 && !joinUserRoom) return;

    void (async () => {
      try {
        await client.connect();
      } catch (error) {
        appWarn('[Cue] startup connect failed:', error);
        return;
      }
      if (joinUserRoom && userRoom) {
        joinedUserRoomRef.current = userRoom;
        try {
          await client.joinChannel(userRoom);
        } catch (error) {
          joinedUserRoomRef.current = null;
          appWarn(`[Cue] startup join ${userRoom} failed:`, error);
        }
      }
      for (const c of toJoin) {
        joinedAllChannelsRef.current.add(c.id);
        try {
          await client.joinChannel(c.id);
        } catch (error) {
          joinedAllChannelsRef.current.delete(c.id);
          appWarn(`[Cue] startup join ${c.id} failed:`, error);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, security.key, security.secret, channels, profile.userId]);

  const addPost = (p: Post, channelId: string) => patchChannel(channelId, (c) => ({ ...c, posts: [p, ...c.posts] }));

  const updateChannelViewType = (viewType: ChannelViewType, channelId: string) =>
    patchChannel(channelId, (c) => ({ ...c, viewType }));

  const updateChannelChartType = (chartType: ChartVariant, channelId: string) =>
    patchChannel(channelId, (c) => ({ ...c, viewChartType: chartType }));

  // ChannelShareScreen's "Let visitors adjust chart filters" toggle - the
  // public /shared/{nickname}/{token} view's chart is locked (owner's
  // saved Channel.chartSettings, read-only) unless this is on. Same
  // patchChannel-is-the-sync pattern as updateChannelViewType just above.
  const updateChannelPublicChartFiltersAllowed = (allowed: boolean, channelId: string) =>
    patchChannel(channelId, (c) => ({ ...c, publicChartFiltersAllowed: allowed }));

  // Patches whichever of `channels`/`viewedChannels` currently holds this
  // channel - a public channel opened from a Search result (not yet
  // followed/joined) lives only in viewedChannels (see ensureChannelLoaded's
  // comment), so a plain `channels`-only patch here used to silently no-op
  // for it: the Follow button's "did *I* already follow this" read-back on
  // mount, and the optimistic patch right after pressing it, both landed
  // nowhere, so the button never showed "Following" for a channel reached
  // this way.
  const applyChannelStats = (channelId: string, patch: Partial<Pick<Channel, 'liked' | 'following' | 'muted' | 'likes' | 'followers' | 'isPublic' | 'ownerIds'>>) => {
    setChannels((prev) => (prev.some((c) => c.id === channelId) ? prev.map((c) => (c.id === channelId ? { ...c, ...patch } : c)) : prev));
    setViewedChannels((prev) => (prev[channelId] ? { ...prev, [channelId]: { ...prev[channelId], ...patch } } : prev));
  };

  // Following a channel adds it to the caller's own user:channels:<userId>
  // index server-side now (see stats/channel_follow.wsl's header comment),
  // so it belongs in the "Channels" list immediately - not just after the
  // next full syncChannels() refresh. A no-op if it's already there
  // (already owned/joined/followed).
  const addChannelToPersonalList = (c: Channel) =>
    setChannels((prev) => (prev.some((existing) => existing.id === c.id) ? prev : [{ ...c, following: true }, ...prev]));

  // The mirror of addChannelToPersonalList, for a non-owner unfollowing a
  // public channel: following was the only thing that put it in their
  // "Channels" list (backend/workflows/stats/channel_unfollow.wsl drops it
  // from user:channels for a non-owner/non-approved-member), so take it
  // out right away instead of waiting for the next syncChannels(). The
  // channel is moved into viewedChannels rather than just dropped, so the
  // details screen the user is still looking at keeps resolving it (via
  // `channel()`'s viewedChannels fallback) and simply shows "Follow"
  // again. A genuine approved member who *also* followed (rare - they'd
  // have had to press Follow after being approved) would lose it from the
  // list here too; the next full syncChannels() puts it back.
  //
  // Takes the full Channel, not just its id, matching
  // addChannelToPersonalList's own signature - it used to re-derive it via
  // `channels.find(...)`, a closure snapshot that's already stale by the
  // time this runs if the caller (e.g. ChannelDetailsScreen's toggleFollow)
  // already applied an unfollow response's fresh follower count moments
  // earlier in the same handler: React batches that setChannels, so this
  // function's own `channels` closure still reflected the PRE-unfollow
  // count, and that stale count is exactly what got written into
  // viewedChannels here - the visible "Followers count doesn't change on
  // unfollow" bug. The caller already has the authoritative post-unfollow
  // object in hand; passing it straight through needs no lookup at all.
  const removeChannelFromPersonalList = (c: Channel) => {
    setViewedChannels((prev) => ({ ...prev, [c.id]: { ...c, following: false } }));
    setChannels((prev) => prev.filter((existing) => existing.id !== c.id));
  };

  const applyTemplateStats = (templateId: string, patch: Partial<Pick<Template, 'liked' | 'likes'>>) =>
    setTemplates((prev) => prev.map((t) => (t.id === templateId ? { ...t, ...patch } : t)));

  // The one sorted view of the channel list every screen reads. Internal
  // mutations (setChannels) keep operating on the unsorted array; this is
  // what leaves the store. See sortChannels for the order.
  const orderedChannels = useMemo(
    () => sortChannels(channels, channelPins, channelOpenedAt, unread.byChannel),
    [channels, channelPins, channelOpenedAt, unread.byChannel]
  );
  // Templates: a plain deterministic order (title, then nickname) - no
  // pinning / priority / recency, just "stop reshuffling".
  const orderedTemplates = useMemo(
    () =>
      (Array.isArray(templates) ? [...templates] : []).sort((a, b) => {
        const at = (a.title || '').toLowerCase();
        const bt = (b.title || '').toLowerCase();
        if (at !== bt) return at < bt ? -1 : 1;
        return a.nickname < b.nickname ? -1 : a.nickname > b.nickname ? 1 : 0;
      }),
    [templates]
  );

  const value = useMemo<StoreContextValue>(
    () => ({
      isLoggedIn,
      authChecked,
      selectedTab,
      themeMode,
      language,
      threeColumnLayout,
      manualRefresh,
      diagnosticsMenuEnabled,
      channels: orderedChannels,
      templates: orderedTemplates,
      mappings,
      channelInvites,
      channelsHasMore,
      templatesHasMore,
      mappingsHasMore,
      messageStore,
      profile,
      security,
      aiSettings,
      tierCatalog,
      billing,
      searchQuery,
      errorMessage,
      isSyncing,
      setSelectedTab,
      setSearchQuery,
      setErrorMessage,
      login,
      verifyTwoFactor,
      verifyAuthenticator,
      loginWithPasskey,
      registerWithPasskey,
      loginWithToken,
      register,
      sendRegisterCode,
      resendRegisterCode,
      sendPasswordReset,
      resetPasswordWithCode,
      logout,
      setThemeMode,
      setLanguage,
      setThreeColumnLayout,
      setManualRefresh,
      setDiagnosticsMenuEnabled,
      setProfile,
      renameMyNickname,
      checkUserNicknameAvailable,
      setSecurity,
      setAiSettings,
      syncSettings,
      refreshBilling,
      startUpgrade,
      openBillingPortalPage,
      syncChannels,
      syncTemplates,
      syncMappings,
      reconnectCue,
      syncChannelInvites,
      acceptInvite,
      declineInvite,
      loadMoreChannels,
      loadMoreTemplates,
      loadMoreMappings,
      loadMoreMessages,
      messagesHasMore,
      channel,
      updateChannel,
      createChannel,
      deleteChannel,
      renameChannel,
      checkChannelNicknameAvailable,
      clearChannelMessages,
      leaveChannel,
      isChannelOwner,
      cloneChannel,
      publishChannelAsTemplate,
      updateTemplate,
      addTemplate,
      deleteTemplate,
      renameTemplate,
      checkTemplateNicknameAvailable,
      isTemplateOwner,
      cloneTemplate,
      applyTemplateStats,
      addMapping,
      updateMapping,
      deleteMapping,
      rotateMappingIngestToken,
      rotateMappingWebhookSecret,
      setMappingEnabled,
      isMappingOwner,
      updateWorkflow,
      addMessage,
      sendMessage,
      sendRoundAction,
      loadMessages,
      subscribeToChannel,
      reportJobs,
      reportJobsMeta,
      loadReportJobs,
      loadMoreReportJobs,
      enqueueReportJob,
      fetchChannelQuestions,
      removeReportQuestion,
      suggestReportQuestions,
      cancelReportJob,
      unreadByChannel: unread.byChannel,
      unreadTotal: unread.total,
      refreshUnread,
      markChannelViewed,
      isChannelPinned,
      setChannelPinned,
      addPost,
      updateChannelViewType,
      updateChannelChartType,
      updateChannelPublicChartFiltersAllowed,
      getChartCompareChannels,
      setChartCompareChannels,
      getReportAdditionalChannels,
      setReportAdditionalChannels,
      setChartSettings,
      applyChannelStats,
      addChannelToPersonalList,
      removeChannelFromPersonalList,
      ensureChannelLoaded,
    }),
    [
      isLoggedIn,
      authChecked,
      selectedTab,
      themeMode,
      language,
      threeColumnLayout,
      manualRefresh,
      diagnosticsMenuEnabled,
      channels,
      orderedChannels,
      templates,
      orderedTemplates,
      mappings,
      channelInvites,
      viewedChannels,
      unread,
      channelPins,
      channelOpenedAt,
      channelsHasMore,
      templatesHasMore,
      mappingsHasMore,
      messageStore,
      profile,
      security,
      aiSettings,
      reportJobs,
      reportJobsMeta,
      tierCatalog,
      billing,
      searchQuery,
      errorMessage,
      isSyncing,
    ]
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreContextValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within a StoreProvider');
  return ctx;
}

// One channel's messages + hasMore, re-rendering the caller only when THAT
// feed changes (not on unrelated store updates).
export function useChannelFeed(channelId: string | undefined): Feed {
  const { messageStore } = useStore();
  return useChannelFeedFrom(messageStore, channelId);
}
